<template>
    <div class="page members-page">
        <div class="page-header">
            <h1>{{ pageTitle }}</h1>
            <p>{{ pageSubtitle }}</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 篩選按鈕 -->
            <div class="toolbar">
                <div class="toolbar-left">
                    <a-button
                        type="text"
                        class="column-settings-icon-btn"
                        v-if="profileConfig.showColumnSettings"
                        @click="showColumnSettingsModal"
                        :title="'欄位設置'"
                    >
                        <template #icon>
                            <TableOutlined />
                        </template>
                    </a-button>
                    <div class="filter-toggle-container">
                        <button
                            @click="toggleFilterPanel"
                            class="filter-toggle-btn"
                            :class="{ active: filterActiveKeys.length > 0 }"
                        >
                            篩選
                        </button>
                    </div>
                </div>
                <a-space>
                    <a-tooltip
                        v-if="profileConfig.allowCreate"
                        :title="
                            hasCreatePermission
                                ? ''
                                : '請在角色權限勾選「新增會員」'
                        "
                    >
                        <span>
                            <a-button
                                type="primary"
                                class="unified-add-btn"
                                :disabled="!hasCreatePermission"
                                @click="onCreateClick"
                            >
                                新增會員
                            </a-button>
                        </span>
                    </a-tooltip>
                    <a-tooltip
                        v-if="profileConfig.allowImport"
                        :title="
                            hasImportPermission
                                ? ''
                                : '請在角色權限勾選「匯入會員資料」'
                        "
                    >
                        <span>
                            <a-button
                                class="unified-secondary-btn"
                                :disabled="!hasImportPermission"
                                @click="onImportClick"
                            >
                                匯入會員
                            </a-button>
                        </span>
                    </a-tooltip>
                    <a-button
                        v-if="canExport"
                        class="unified-secondary-btn"
                        @click="handleExport"
                    >
                        匯出會員
                    </a-button>
                </a-space>
            </div>

            <!-- 篩選面板 -->
            <a-collapse
                v-model:activeKey="filterActiveKeys"
                class="filter-panel"
                :bordered="false"
                :ghost="true"
            >
                <a-collapse-panel key="1" class="filter-panel-content">
                    <div class="filter-content">
                        <a-space wrap size="middle" class="filter-items-full">
                            <a-select
                                v-if="filterVisible('referral_department_id')"
                                v-model:value="filters.referral_department_id"
                                placeholder="推薦事業部"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[160px]"
                                :options="departmentOptions"
                                :virtual="false"
                                show-search
                                option-filter-prop="label"
                            />
                            <a-select
                                v-if="filterVisible('department_id')"
                                v-model:value="filters.department_id"
                                placeholder="輔導事業部"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[160px]"
                                :options="departmentOptions"
                                :virtual="false"
                                show-search
                                option-filter-prop="label"
                            />
                            <a-select
                                v-if="filterVisible('rank_id')"
                                v-model:value="filters.rank_id"
                                placeholder="聘階"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[160px]"
                                :options="rankOptions"
                                :virtual="false"
                                show-search
                                option-filter-prop="label"
                            />
                            <a-select
                                v-if="filterVisible('status')"
                                v-model:value="filters.status"
                                placeholder="狀態"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[120px]"
                                :options="statusOptions"
                            />
                            <a-select
                                v-if="filterVisible('identity')"
                                v-model:value="filters.identity"
                                placeholder="身份"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[120px]"
                                :options="identityOptions"
                            />
                            <a-select
                                v-if="filterVisible('deregistered')"
                                v-model:value="filters.deregistered"
                                placeholder="除籍"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[120px]"
                                :options="deregisteredOptions"
                            />
                            <a-select
                                v-if="filterVisible('has_line')"
                                v-model:value="filters.has_line"
                                placeholder="LINE 綁定"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[140px]"
                                :options="lineBindingOptions"
                            />
                            <a-input
                                v-if="filterVisible('name') && useFuzzyNameFilter"
                                v-model:value="filters.name"
                                placeholder="姓名（模糊搜尋）"
                                allow-clear
                                size="middle"
                                class="filter-input min-w-[180px]"
                            />
                            <a-select
                                v-else-if="filterVisible('name')"
                                show-search
                                :filter-option="false"
                                :options="nameOptions"
                                :loading="nameLoading"
                                v-model:value="filters.name"
                                placeholder="姓名"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[180px]"
                                @search="onNameSearch"
                                @clear="onNameClear"
                            />
                            <a-input
                                v-if="filterVisible('email')"
                                v-model:value="filters.email"
                                placeholder="Email（模糊搜尋）"
                                allow-clear
                                size="middle"
                                class="filter-input min-w-[200px]"
                            />
                            <a-input
                                v-if="filterVisible('phone')"
                                v-model:value="filters.phone"
                                placeholder="手機（模糊搜尋）"
                                allow-clear
                                size="middle"
                                class="filter-input min-w-[160px]"
                            />
                            <a-input
                                v-if="filterVisible('id_number')"
                                v-model:value="filters.id_number"
                                placeholder="身分證／居留證（模糊搜尋）"
                                size="middle"
                                class="filter-input id-number-filter-input"
                            />
                            <a-select
                                v-if="filterVisible('referral_id')"
                                show-search
                                :filter-option="false"
                                :options="referralOptions"
                                :loading="referralLoading"
                                v-model:value="filters.referral_id"
                                placeholder="推薦人"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[180px]"
                                @search="onReferralSearch"
                                @clear="onReferralClear"
                            />
                            <a-select
                                v-if="filterVisible('mentor_id')"
                                show-search
                                :filter-option="false"
                                :options="mentorOptions"
                                :loading="mentorLoading"
                                v-model:value="filters.mentor_id"
                                placeholder="輔導人"
                                allow-clear
                                size="middle"
                                class="status-select min-w-[180px]"
                                @search="onMentorSearch"
                                @clear="onMentorClear"
                            />
                            <a-date-picker
                                v-if="filterVisible('assigned_at_range')"
                                v-model:value="filters.assigned_at_from"
                                placeholder="起聘日起"
                                value-format="YYYY-MM-DD"
                                size="middle"
                                class="min-w-[140px]"
                            />
                            <a-date-picker
                                v-if="filterVisible('assigned_at_range')"
                                v-model:value="filters.assigned_at_to"
                                placeholder="起聘日止"
                                value-format="YYYY-MM-DD"
                                size="middle"
                                class="min-w-[140px]"
                            />
                            <a-input
                                v-if="filterVisible('referral_code')"
                                v-model:value="filters.referral_code"
                                placeholder="推薦碼"
                                allow-clear
                                size="middle"
                                class="filter-input min-w-[140px]"
                            />
                        </a-space>

                        <!-- 操作按鈕 -->
                        <div class="filter-actions">
                            <a-button
                                size="middle"
                                class="unified-search-btn"
                                @click="handleSearch"
                            >
                                查詢
                            </a-button>
                            <a-button
                                size="middle"
                                class="unified-reset-btn"
                                @click="handleReset"
                            >
                                重置
                            </a-button>
                        </div>
                    </div>
                </a-collapse-panel>
            </a-collapse>

            <!-- 會員列表表格 -->
            <DataTable
                title=""
                :columns="columns"
                :data-source="users"
                :pagination="pagination"
                :scroll="{ x: 'max-content' }"
                @refresh="loadUsers"
                @change="handleTableChange"
                class="members-table"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'name'">
                        <div class="user-name-cell">
                            <span class="user-name">
                                {{ record.name || '-' }}
                            </span>
                            <span
                                class="gender-icon"
                                :style="{
                                    color: getGenderInfo(record.gender).color
                                }"
                            >
                                <ManOutlined
                                    v-if="record.gender === 'male'"
                                    class="gender-icon-content"
                                />
                                <WomanOutlined
                                    v-else-if="record.gender === 'female'"
                                    class="gender-icon-content"
                                />
                            </span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'email'">
                        <div class="verified-cell">
                            <span class="email-text">
                                {{ record.email || '-' }}
                            </span>
                            <span
                                v-if="
                                    record.email &&
                                    record.email.trim().length > 0
                                "
                                class="verified-icon"
                            >
                                <CheckCircleOutlined
                                    v-if="record.email_verified_at"
                                    style="color: #52c41a"
                                />
                                <CloseCircleOutlined
                                    v-else
                                    style="color: #ff4d4f"
                                />
                            </span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'phone'">
                        <div class="verified-cell">
                            <span class="phone-text">
                                {{ record.phone || '-' }}
                            </span>
                            <span
                                v-if="
                                    record.phone &&
                                    record.phone.trim().length > 0
                                "
                                class="verified-icon"
                            >
                                <CheckCircleOutlined
                                    v-if="record.phone_verified_at"
                                    style="color: #52c41a"
                                />
                                <CloseCircleOutlined
                                    v-else
                                    style="color: #ff4d4f"
                                />
                            </span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'birthdate'">
                        <span class="birthday-text">
                            {{ record.birthdate || '-' }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'id_number'">
                        <div class="id-number-cell">
                            <span class="id-number-text">
                                {{
                                    record.id_number && record.id_number.trim()
                                        ? idNumberVisible[record.id]
                                            ? record.id_number
                                            : maskIdNumber(record.id_number)
                                        : '-'
                                }}
                            </span>
                            <span
                                v-if="
                                    record.id_number && record.id_number.trim()
                                "
                                class="eye-icon"
                                @click="toggleIdNumber(record)"
                            >
                                <EyeOutlined
                                    v-if="!idNumberVisible[record.id]"
                                />
                                <EyeInvisibleOutlined v-else />
                            </span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'referral_department'">
                        <span class="department-text">
                            {{ record.referral_department || '-' }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'department'">
                        <span class="department-text">
                            {{ record.department || '-' }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'mentor'">
                        <span
                            class="mentor-text clickable-link"
                            @click="setMentorFilter(record)"
                        >
                            {{ record.mentor || '-' }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'partner_assigned_at'">
                        <span>
                            {{
                                formatAssignedAtDisplay(
                                    record.partner_assigned_at
                                )
                            }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'referral'">
                        <span
                            class="referral-text clickable-link"
                            @click="setReferralFilter(record)"
                        >
                            {{ record.referral || '-' }}
                        </span>
                    </template>
                    <template v-else-if="column.key === 'identity'">
                        <!-- 夥伴：直接顯示聘階名稱，標籤用聘階顏色；淡色時加黑框黑字 -->
                        <a-tag
                            v-if="
                                record.identity === 'partner' &&
                                (record.rank || record.rank_id)
                            "
                            :color="getRankColor(record.rank_id)"
                            :class="{
                                'rank-tag-light-bg': isRankColorLight(
                                    getRankColor(record.rank_id)
                                ),
                                'rank-tag-near-white': isRankColorNearWhite(
                                    getRankColor(record.rank_id)
                                )
                            }"
                        >
                            {{ record.rank || getRankName(record.rank_id) }}
                        </a-tag>
                        <a-tag v-else :color="getIdentityTagColor(record.identity)">
                            {{ getIdentityName(record.identity) }}
                        </a-tag>
                        <a-tag v-if="record.is_deregistered" color="orange" class="ml-1">
                            除籍
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'status'">
                        <a-tag
                            :color="
                                record.status === true || record.status === 1
                                    ? 'green'
                                    : 'red'
                            "
                        >
                            {{
                                record.status === true || record.status === 1
                                    ? '啟用'
                                    : '停用'
                            }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'action'">
                        <a-space class="action-buttons">
                            <a-button
                                v-if="canViewDetail && profileActionVisible('detail')"
                                size="small"
                                class="action-btn-view"
                                @click="viewDetail(record)"
                            >
                                詳情
                            </a-button>
                            <a-button
                                v-if="
                                    canEdit &&
                                    profileActionVisible('password') &&
                                    canShowPasswordAction(record)
                                "
                                size="small"
                                class="action-btn-edit"
                                @click="showChangePasswordModal(record)"
                            >
                                更改密碼
                            </a-button>
                            <a-button
                                v-if="profileActionVisible('orgChart') && canViewOrgChart"
                                size="small"
                                class="action-btn-view"
                                @click="openOrgChart(record)"
                            >
                                組織圖
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 會員詳情 Modal -->
        <a-modal
            v-model:open="detailModalVisible"
            title="會員詳情"
            :footer="null"
            width="700px"
        >
            <div v-if="userDetail" class="detail-content">
                <a-descriptions :column="1" bordered>
                    <a-descriptions-item label="會員名稱">
                        {{ userDetail.name }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Email">
                        {{ userDetail.email }}
                    </a-descriptions-item>
                    <a-descriptions-item label="手機號碼">
                        {{ userDetail.phone || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="會員身份">
                        <a-tag :color="getIdentityTagColor(userDetail.identity)">
                            {{ getIdentityName(userDetail.identity) }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="聘階">
                        {{ userDetail.rank || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="輔導事業部">
                        {{ userDetail.department || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="當前等級">
                        {{ userDetail.level || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="輔導人">
                        {{ userDetail.mentor || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="推薦人">
                        {{ userDetail.referral || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="經驗值">
                        {{ userDetail.exp || 0 }}
                    </a-descriptions-item>
                    <a-descriptions-item label="狀態">
                        <a-tag
                            :color="
                                userDetail.status === true ||
                                userDetail.status === 1
                                    ? 'green'
                                    : 'red'
                            "
                        >
                            {{
                                userDetail.status === true ||
                                userDetail.status === 1
                                    ? '啟用'
                                    : '停用'
                            }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="註冊時間">
                        {{ userDetail.created_at }}
                    </a-descriptions-item>
                    <a-descriptions-item label="最後登入">
                        {{ userDetail.last_login_at || '-' }}
                    </a-descriptions-item>
                    <a-descriptions-item label="推薦人">
                        {{ userDetail.referral || '-' }}
                    </a-descriptions-item>
                </a-descriptions>
            </div>
        </a-modal>

        <!-- 編輯會員 Modal -->
        <a-modal
            v-model:open="editModalVisible"
            title="編輯會員"
            :confirm-loading="editModalLoading"
            ok-text="確定"
            cancel-text="取消"
            @ok="handleEditOk"
            @cancel="handleEditCancel"
        >
            <a-form
                ref="editFormRef"
                :model="editFormState"
                :rules="editFormRules"
                layout="vertical"
            >
                <a-form-item label="經驗值" name="exp">
                    <a-input-number
                        v-model:value="editFormState.exp"
                        :min="0"
                        style="width: 100%"
                    />
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 積分管理 Modal -->
        <a-modal
            v-model:open="pointsModalVisible"
            title="積分調整"
            :confirm-loading="pointsModalLoading"
            ok-text="確定"
            cancel-text="取消"
            @ok="handlePointsOk"
            @cancel="handlePointsCancel"
        >
            <div v-if="currentUser">
                <p>
                    <strong>會員：</strong>
                    {{ currentUser.name }} ({{ currentUser.email }})
                </p>
                <p>
                    <strong>目前積分：</strong>
                    {{ currentUser.points || 0 }}
                </p>
                <a-divider />
                <a-form
                    ref="pointsFormRef"
                    :model="pointsFormState"
                    :rules="pointsFormRules"
                    layout="vertical"
                >
                    <a-form-item label="積分類型" name="point_type">
                        <a-select
                            v-model:value="pointsFormState.point_type"
                            placeholder="請選擇積分類型"
                        >
                            <a-select-option value="CP">
                                CP 積分
                            </a-select-option>
                            <a-select-option value="PP">
                                PP 積分
                            </a-select-option>
                            <a-select-option value="TP">
                                TP 積分
                            </a-select-option>
                            <a-select-option value="GP">
                                GP 積分
                            </a-select-option>
                            <a-select-option value="DIAMOND">
                                鑽石
                            </a-select-option>
                            <a-select-option value="GOLD">金幣</a-select-option>
                        </a-select>
                    </a-form-item>
                    <a-form-item label="調整類型" name="type">
                        <a-radio-group v-model:value="pointsFormState.type">
                            <a-radio value="add">增加積分</a-radio>
                            <a-radio value="subtract">減少積分</a-radio>
                        </a-radio-group>
                    </a-form-item>
                    <a-form-item label="積分數量" name="points">
                        <a-input-number
                            v-model:value="pointsFormState.points"
                            :min="1"
                            style="width: 100%"
                            placeholder="請輸入積分數量"
                        />
                    </a-form-item>
                    <a-form-item label="調整原因" name="reason">
                        <a-textarea
                            v-model:value="pointsFormState.reason"
                            placeholder="請輸入調整原因"
                            :rows="4"
                        />
                    </a-form-item>
                </a-form>
            </div>
        </a-modal>

        <!-- 匯入會員 Modal -->
        <a-modal
            v-model:open="importModalVisible"
            title="匯入會員"
            :confirm-loading="importModalLoading"
            ok-text="確定"
            cancel-text="取消"
            @ok="handleImportOk"
            @cancel="handleImportCancel"
        >
            <div class="import-modal-content">
                <div class="template-download-section">
                    <a-button
                        type="primary"
                        @click="handleDownloadTemplate"
                        :loading="templateDownloadLoading"
                        class="template-download-btn"
                    >
                        <DownloadOutlined />
                        下載匯入範例
                    </a-button>
                    <p class="template-hint">
                        請先下載範例檔案，填寫資料後再上傳
                    </p>
                </div>

                <a-divider />

                <a-upload-dragger
                    v-model:fileList="importFileList"
                    :multiple="false"
                    :before-upload="beforeUpload"
                    :action="null"
                    accept=".xlsx,.xls,.csv"
                >
                    <p class="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p class="ant-upload-text">點擊或拖拽檔案到此處上傳</p>
                    <p class="ant-upload-hint">
                        支援 Excel (.xlsx, .xls) 或 CSV 檔案
                    </p>
                </a-upload-dragger>
            </div>
        </a-modal>

        <!-- 匯入結果顯示 Modal -->
        <a-modal
            v-model:open="importErrorsModalVisible"
            :title="getImportResultTitle()"
            width="800px"
            :footer="null"
            class="import-errors-modal"
            :class="getImportResultModalClass()"
        >
            <div class="import-result-content">
                <!-- 主要狀態訊息 -->
                <div
                    class="result-status-message"
                    :class="getImportResultStatusClass()"
                >
                    <component
                        :is="getImportResultIcon()"
                        class="status-icon"
                    />
                    <div class="status-text">
                        <div class="status-title">
                            {{ getImportResultTitle() }}
                        </div>
                        <div
                            v-if="importResult.successCount > 0"
                            class="status-detail"
                        >
                            成功匯入 {{ importResult.successCount }} 筆資料
                            <span
                                v-if="
                                    importResult.createdCount > 0 ||
                                    importResult.updatedCount > 0
                                "
                            >
                                （
                                <span v-if="importResult.createdCount > 0">
                                    新增 {{ importResult.createdCount }} 筆
                                </span>
                                <span
                                    v-if="
                                        importResult.createdCount > 0 &&
                                        importResult.updatedCount > 0
                                    "
                                >
                                    、
                                </span>
                                <span v-if="importResult.updatedCount > 0">
                                    更新 {{ importResult.updatedCount }} 筆
                                </span>
                                ）
                            </span>
                        </div>
                        <div
                            v-else-if="importResult.errorMessage"
                            class="status-detail"
                        >
                            {{ importResult.errorMessage }}
                        </div>
                    </div>
                </div>

                <!-- 結果摘要 -->
                <div
                    v-if="
                        importResult.successCount > 0 ||
                        importResult.failureCount > 0
                    "
                    class="result-summary"
                >
                    <div
                        v-if="importResult.createdCount > 0"
                        class="summary-item created-info"
                    >
                        <CheckCircleOutlined class="created-icon" />
                        <div class="summary-content">
                            <span class="summary-label">新增</span>
                            <span class="summary-value">
                                {{ importResult.createdCount }}
                            </span>
                            <span class="summary-unit">筆</span>
                        </div>
                    </div>
                    <div
                        v-if="importResult.updatedCount > 0"
                        class="summary-item updated-info"
                    >
                        <CheckCircleOutlined class="updated-icon" />
                        <div class="summary-content">
                            <span class="summary-label">更新</span>
                            <span class="summary-value">
                                {{ importResult.updatedCount }}
                            </span>
                            <span class="summary-unit">筆</span>
                        </div>
                    </div>
                    <div
                        v-if="importResult.failureCount > 0"
                        class="summary-item error-info"
                    >
                        <CloseCircleOutlined class="error-icon" />
                        <div class="summary-content">
                            <span class="summary-label">失敗</span>
                            <span class="summary-value">
                                {{ importResult.failureCount }}
                            </span>
                            <span class="summary-unit">筆</span>
                        </div>
                    </div>
                </div>

                <!-- 失敗詳情 -->
                <div
                    v-if="
                        importResult.failures &&
                        importResult.failures.length > 0
                    "
                    class="errors-section"
                >
                    <a-divider />
                    <h4 class="section-title">
                        <ExclamationCircleOutlined class="title-icon" />
                        失敗明細
                    </h4>
                    <div class="errors-container">
                        <div
                            v-for="(failure, index) in importResult.failures"
                            :key="index"
                            class="error-item"
                        >
                            <div class="error-header">
                                <span class="error-index">
                                    {{ index + 1 }}.
                                </span>
                                <span v-if="failure.row" class="error-row">
                                    第 {{ failure.row }} 行
                                </span>
                                <span v-if="failure.name" class="error-name">
                                    {{ failure.name }}
                                </span>
                            </div>
                            <div class="error-message">
                                <span class="error-reason-label">
                                    錯誤原因：
                                </span>
                                <span class="error-reason-text">
                                    {{ failure.reason || '未知錯誤' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 缺少資料 -->
                <div
                    v-if="
                        importResult.missingData &&
                        importResult.missingData.length > 0
                    "
                    class="missing-data-section"
                >
                    <a-divider />
                    <div class="missing-data-info">
                        <WarningOutlined class="info-icon" />
                        <p class="info-text">
                            這些身分證號碼被引用但 CSV
                            中沒有完整資料，系統已建立臨時用戶
                        </p>
                    </div>
                    <div class="missing-data-container">
                        <div
                            v-for="(item, index) in importResult.missingData"
                            :key="index"
                            class="missing-data-item"
                        >
                            <span class="missing-data-text">{{ item }}</span>
                        </div>
                    </div>
                </div>

                <!-- 操作按鈕 -->
                <div class="modal-actions">
                    <a-button type="primary" @click="handleImportResultClose">
                        我知道了
                    </a-button>
                </div>
            </div>
        </a-modal>

        <!-- 聘階歷史 Modal -->
        <a-modal
            v-model:open="rankHistoryModalVisible"
            title="聘階歷史記錄"
            :footer="null"
            width="800px"
            class="rank-history-modal"
        >
            <div v-if="currentUser" class="rank-history-content">
                <div class="user-info-header">
                    <h3>{{ currentUser.name }} 的聘階變更歷史</h3>
                    <p>Email: {{ currentUser.email }}</p>
                </div>

                <a-spin :spinning="rankHistoryLoading">
                    <a-table
                        :columns="rankHistoryColumns"
                        :data-source="userRankHistory"
                        :pagination="false"
                        size="small"
                        class="rank-history-table"
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'from_rank'">
                                <a-tag v-if="record.from_rank" color="blue">
                                    {{ record.from_rank }}
                                </a-tag>
                                <span v-else>-</span>
                            </template>
                            <template v-if="column.key === 'to_rank'">
                                <a-tag color="green">
                                    {{ record.to_rank }}
                                </a-tag>
                            </template>
                            <template v-if="column.key === 'reason'">
                                <span class="reason-text">
                                    {{ record.reason || '-' }}
                                </span>
                            </template>
                            <template v-if="column.key === 'created_at'">
                                <span class="date-text">
                                    {{ formatDate(record.created_at) }}
                                </span>
                            </template>
                        </template>
                    </a-table>

                    <div
                        v-if="
                            !rankHistoryLoading && userRankHistory.length === 0
                        "
                        class="empty-state"
                    >
                        <a-empty description="暫無聘階變更記錄" />
                    </div>
                </a-spin>
            </div>
        </a-modal>

        <!-- 新增會員 Modal -->
        <a-modal
            v-model:open="createModalVisible"
            title="新增會員"
            :confirm-loading="createModalLoading"
            ok-text="確定"
            cancel-text="取消"
            width="700px"
            :ok-button-props="{ size: 'large' }"
            :cancel-button-props="{ size: 'large' }"
            @ok="handleCreateOk"
            @cancel="handleCreateCancel"
            class="create-member-modal"
        >
            <a-form
                ref="createFormRef"
                :model="createFormState"
                :rules="createFormRules"
                layout="vertical"
                class="create-member-form"
            >
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="姓名" name="name">
                            <a-input
                                v-model:value="createFormState.name"
                                placeholder="請輸入姓名"
                                size="large"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="Email" name="email">
                            <a-input
                                v-model:value="createFormState.email"
                                placeholder="請輸入 Email"
                                size="large"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>

                <a-form-item label="性別" name="gender">
                    <a-select
                        v-model:value="createFormState.gender"
                        placeholder="請選擇性別"
                        size="large"
                    >
                        <a-select-option :value="1">男性</a-select-option>
                        <a-select-option :value="0">女性</a-select-option>
                    </a-select>
                </a-form-item>

                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="手機號碼" name="phone">
                            <a-input
                                v-model:value="createFormState.phone"
                                placeholder="請輸入手機號碼"
                                size="large"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="生日" name="birthdate">
                            <a-date-picker
                                v-model:value="createFormState.birthdate"
                                placeholder="請選擇生日"
                                style="width: 100%"
                                size="large"
                                format="YYYY-MM-DD"
                                value-format="YYYY-MM-DD"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>

                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item :label="TAIWAN_ID_LABEL" name="id_number">
                            <a-input
                                v-model:value="createFormState.id_number"
                                placeholder="本國身分證或居留證統一證號"
                                size="large"
                                class="id-number-input"
                                @input="handleIdNumberInput"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="Line ID" name="line_id">
                            <a-input
                                v-model:value="createFormState.line_id"
                                placeholder="請輸入 Line ID"
                                size="large"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>

                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item
                            label="會員身分"
                            name="identity"
                            :rules="[
                                {
                                    required: true,
                                    message: '請選擇會員身分',
                                    trigger: 'change'
                                }
                            ]"
                        >
                            <a-select
                                v-model:value="createFormState.identity"
                                placeholder="請選擇會員身分"
                                allowClear
                                size="large"
                            >
                                <a-select-option value="guest">
                                    遊客
                                </a-select-option>
                                <a-select-option value="student">
                                    學員
                                </a-select-option>
                                <a-select-option value="partner">
                                    夥伴
                                </a-select-option>
                                <a-select-option value="manager">
                                    管理員
                                </a-select-option>
                            </a-select>
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="帳號狀態" name="status">
                            <a-switch
                                v-model:checked="createFormState.status"
                                checked-children="啟用"
                                un-checked-children="停用"
                            />
                        </a-form-item>
                    </a-col>
                </a-row>

                <!-- 夥伴相關資料（僅 identity=partner 時顯示，且為必填） -->
                <template v-if="createFormState.identity === 'partner'">
                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-form-item
                                label="輔導事業部"
                                :name="['partner', 'department_id']"
                                :rules="
                                    createFormState.identity === 'partner'
                                        ? [
                                              {
                                                  required: true,
                                                  message: '請選擇輔導事業部',
                                                  trigger: 'change'
                                              }
                                          ]
                                        : []
                                "
                            >
                                <a-select
                                    v-model:value="
                                        createFormState.partner.department_id
                                    "
                                    placeholder="請選擇輔導事業部"
                                    size="large"
                                    :options="departmentOptions"
                                    :virtual="false"
                                    show-search
                                    option-filter-prop="label"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item
                                label="聘階"
                                :name="['partner', 'rank_id']"
                                :rules="
                                    createFormState.identity === 'partner'
                                        ? [
                                              {
                                                  required: true,
                                                  message: '請選擇聘階',
                                                  trigger: 'change'
                                              }
                                          ]
                                        : []
                                "
                            >
                                <a-select
                                    v-model:value="
                                        createFormState.partner.rank_id
                                    "
                                    placeholder="請選擇聘階"
                                    size="large"
                                    :options="rankOptions"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-form-item
                                label="推薦人"
                                :name="['partner', 'referral_id']"
                                :rules="
                                    createFormState.identity === 'partner'
                                        ? [
                                              {
                                                  required: true,
                                                  message: '請選擇推薦人',
                                                  trigger: 'change'
                                              }
                                          ]
                                        : []
                                "
                            >
                                <a-select
                                    v-model:value="
                                        createFormState.partner.referral_id
                                    "
                                    placeholder="請選擇推薦人"
                                    show-search
                                    :filter-option="false"
                                    :options="referralOptions"
                                    :loading="referralLoading"
                                    size="large"
                                    @search="onPartnerReferralSearch"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item
                                label="輔導人"
                                :name="['partner', 'mentor_id']"
                                :rules="
                                    createFormState.identity === 'partner'
                                        ? [
                                              {
                                                  required: true,
                                                  message: '請選擇輔導人',
                                                  trigger: 'change'
                                              }
                                          ]
                                        : []
                                "
                            >
                                <a-select
                                    v-model:value="
                                        createFormState.partner.mentor_id
                                    "
                                    placeholder="請選擇輔導人"
                                    show-search
                                    :filter-option="false"
                                    :options="mentorOptions"
                                    :loading="mentorLoading"
                                    size="large"
                                    @search="onPartnerMentorSearch"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-form-item
                                :name="['partner', 'recruiter_id']"
                                :rules="
                                    createFormState.identity === 'partner'
                                        ? [
                                              {
                                                  required: true,
                                                  message: '請選擇招募人',
                                                  trigger: 'change'
                                              }
                                          ]
                                        : []
                                "
                            >
                                <template #label>
                                    <span class="org-field-label">
                                        招募人
                                        <a
                                            class="org-copy-link"
                                            :class="{
                                                'is-disabled':
                                                    !createFormState.partner
                                                        .referral_id
                                            }"
                                            href="#"
                                            @click.prevent="
                                                copyCreateReferralToRecruiter
                                            "
                                        >
                                            同推薦人
                                        </a>
                                    </span>
                                </template>
                                <a-select
                                    v-model:value="
                                        createFormState.partner.recruiter_id
                                    "
                                    placeholder="請選擇招募人"
                                    show-search
                                    allow-clear
                                    :filter-option="false"
                                    :options="recruiterOptions"
                                    :loading="recruiterLoading"
                                    size="large"
                                    @search="onPartnerRecruiterSearch"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item
                                :name="['partner', 'referral_department_id']"
                                extra="未填則依推薦線自動推導"
                            >
                                <template #label>
                                    <span class="org-field-label">
                                        推薦事業部
                                        <a
                                            class="org-copy-link"
                                            :class="{
                                                'is-disabled':
                                                    !createFormState.partner
                                                        .referral_id
                                            }"
                                            href="#"
                                            @click.prevent="
                                                copyCreateReferralDeptFromReferrer
                                            "
                                        >
                                            同推薦人
                                        </a>
                                    </span>
                                </template>
                                <a-select
                                    v-model:value="
                                        createFormState.partner
                                            .referral_department_id
                                    "
                                    placeholder="請選擇推薦事業部（選填）"
                                    allow-clear
                                    size="large"
                                    :options="departmentOptions"
                                    :virtual="false"
                                    show-search
                                    option-filter-prop="label"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-form-item
                                label="夥伴狀態"
                                :name="['partner', 'status']"
                            >
                                <a-switch
                                    v-model:checked="
                                        createFormState.partner.status
                                    "
                                    checked-children="啟用"
                                    un-checked-children="停用"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item
                                label="起聘日期"
                                :name="['partner', 'assigned_at']"
                                :rules="
                                    createFormState.identity === 'partner'
                                        ? [
                                              {
                                                  required: true,
                                                  message: '請選擇起聘日期',
                                                  trigger: 'change'
                                              }
                                          ]
                                        : []
                                "
                            >
                                <a-date-picker
                                    v-model:value="
                                        createFormState.partner.assigned_at
                                    "
                                    placeholder="請選擇起聘日期"
                                    style="width: 100%"
                                    size="large"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>
                </template>

                <!-- 學員／遊客：選填組織欄位 -->
                <template
                    v-if="
                        createFormState.identity === 'student' ||
                        createFormState.identity === 'guest'
                    "
                >
                    <a-row
                        v-if="createFormState.identity === 'student'"
                        :gutter="16"
                    >
                        <a-col :span="12">
                            <a-form-item
                                label="輔導事業部"
                                :name="['partner', 'department_id']"
                            >
                                <a-select
                                    v-model:value="
                                        createFormState.partner.department_id
                                    "
                                    placeholder="請選擇輔導事業部（選填）"
                                    allow-clear
                                    size="large"
                                    :options="departmentOptions"
                                    :virtual="false"
                                    show-search
                                    option-filter-prop="label"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item
                                :name="['partner', 'referral_department_id']"
                            >
                                <template #label>
                                    <span class="org-field-label">
                                        推薦事業部
                                        <a
                                            class="org-copy-link"
                                            :class="{
                                                'is-disabled':
                                                    !createFormState.partner
                                                        .referral_id
                                            }"
                                            href="#"
                                            @click.prevent="
                                                copyCreateReferralDeptFromReferrer
                                            "
                                        >
                                            同推薦人
                                        </a>
                                    </span>
                                </template>
                                <a-select
                                    v-model:value="
                                        createFormState.partner
                                            .referral_department_id
                                    "
                                    placeholder="請選擇推薦事業部（選填）"
                                    allow-clear
                                    size="large"
                                    :options="departmentOptions"
                                    :virtual="false"
                                    show-search
                                    option-filter-prop="label"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <a-row
                        v-if="createFormState.identity === 'guest'"
                        :gutter="16"
                    >
                        <a-col :span="12">
                            <a-form-item
                                label="推薦事業部"
                                :name="['partner', 'referral_department_id']"
                            >
                                <a-select
                                    v-model:value="
                                        createFormState.partner
                                            .referral_department_id
                                    "
                                    placeholder="請選擇推薦事業部（選填）"
                                    allow-clear
                                    size="large"
                                    :options="departmentOptions"
                                    :virtual="false"
                                    show-search
                                    option-filter-prop="label"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-form-item
                                label="推薦人"
                                :name="['partner', 'referral_id']"
                            >
                                <a-select
                                    v-model:value="
                                        createFormState.partner.referral_id
                                    "
                                    placeholder="請選擇推薦人（選填）"
                                    show-search
                                    allow-clear
                                    :filter-option="false"
                                    :options="referralOptions"
                                    :loading="referralLoading"
                                    size="large"
                                    @search="onPartnerReferralSearch"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="12">
                            <a-form-item
                                :name="['partner', 'recruiter_id']"
                                extra="未填且有推薦人時預設為推薦人"
                            >
                                <template #label>
                                    <span class="org-field-label">
                                        招募人
                                        <a
                                            class="org-copy-link"
                                            :class="{
                                                'is-disabled':
                                                    !createFormState.partner
                                                        .referral_id
                                            }"
                                            href="#"
                                            @click.prevent="
                                                copyCreateReferralToRecruiter
                                            "
                                        >
                                            同推薦人
                                        </a>
                                    </span>
                                </template>
                                <a-select
                                    v-model:value="
                                        createFormState.partner.recruiter_id
                                    "
                                    placeholder="請選擇招募人（選填）"
                                    show-search
                                    allow-clear
                                    :filter-option="false"
                                    :options="recruiterOptions"
                                    :loading="recruiterLoading"
                                    size="large"
                                    @search="onPartnerRecruiterSearch"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>
                </template>
            </a-form>
        </a-modal>

        <!-- 顯示帳號密碼 Modal -->
        <a-modal
            v-model:open="accountPasswordModalVisible"
            title="帳號資訊"
            :footer="null"
            width="500px"
            class="account-password-modal"
        >
            <div v-if="createdAccountInfo" class="account-info-content">
                <div class="success-icon-wrapper">
                    <CheckCircleOutlined class="success-icon" />
                </div>
                <h3 class="success-title">會員建立成功！</h3>
                <p class="success-subtitle">請將以下帳號資訊提供給新會員</p>

                <div class="account-info-card">
                    <div class="info-item">
                        <label class="info-label">帳號（Email）：</label>
                        <a-input-group compact class="copy-input-group">
                            <a-input
                                :value="createdAccountInfo.email"
                                readonly
                                class="info-input flex-1"
                            />
                            <a-button
                                type="primary"
                                @click="
                                    copyToClipboard(createdAccountInfo.email)
                                "
                                class="copy-btn"
                            >
                                複製
                            </a-button>
                        </a-input-group>
                    </div>

                    <div class="info-item">
                        <label class="info-label">密碼：</label>
                        <a-input-group compact class="copy-input-group">
                            <a-input-password
                                :value="createdAccountInfo.password"
                                readonly
                                class="info-input flex-1"
                            />
                            <a-button
                                type="primary"
                                @click="
                                    copyToClipboard(createdAccountInfo.password)
                                "
                                class="copy-btn"
                            >
                                複製
                            </a-button>
                        </a-input-group>
                    </div>
                </div>

                <div class="warning-box">
                    <p class="warning-text">
                        ⚠️ 請妥善保管此密碼資訊，僅此一次顯示機會
                    </p>
                </div>

                <div class="modal-actions">
                    <a-button
                        type="primary"
                        @click="accountPasswordModalVisible = false"
                        block
                    >
                        我已記錄
                    </a-button>
                </div>
            </div>
        </a-modal>

        <!-- 夥伴組織圖 Modal -->
        <a-modal
            v-model:open="orgModalVisible"
            title="夥伴組織圖"
            :width="1000"
            :footer="null"
            destroy-on-close
            wrapClassName="orgchart-modal vaporwave-modal"
        >
            <a-spin :spinning="orgChartLoading">
                <div class="org-chart-modal-body">
                    <OrgChart
                        ref="orgChartRef"
                        :key="orgChartKey"
                        :data="orgChartData"
                        :line-type="orgLineType"
                        @lineTypeChange="onOrgLineTypeChange"
                    />
                </div>
            </a-spin>
        </a-modal>

        <!-- 欄位設置 Modal -->
        <a-modal
            v-if="profileConfig.showColumnSettings"
            v-model:open="columnSettingsModalVisible"
            title="欄位顯示設置"
            width="500px"
        >
            <div class="column-settings-content">
                <p class="column-settings-hint">
                    選擇要在表格中顯示的欄位（預設全部顯示）
                </p>
                <a-checkbox-group
                    v-model:value="visibleColumns"
                    class="column-checkbox-group"
                >
                    <a-row :gutter="[16, 12]">
                        <a-col
                            :span="12"
                            v-for="col in allColumns"
                            :key="col.key"
                        >
                            <a-checkbox
                                :value="col.key"
                                :disabled="col.key === 'action'"
                            >
                                {{ col.title }}
                                <span
                                    v-if="col.key === 'action'"
                                    class="required-badge"
                                >
                                    （必選）
                                </span>
                            </a-checkbox>
                        </a-col>
                    </a-row>
                </a-checkbox-group>
            </div>
            <template #footer>
                <a-button @click="columnSettingsModalVisible = false">
                    取消
                </a-button>
                <a-button type="primary" @click="saveColumnSettings">
                    確定
                </a-button>
            </template>
        </a-modal>

        <!-- 更改密碼 Modal -->
        <a-modal
            v-model:open="changePasswordModalVisible"
            title="更改密碼"
            :confirm-loading="changePasswordModalLoading"
            ok-text="確定"
            cancel-text="取消"
            width="500px"
            @ok="handleChangePasswordOk"
            @cancel="handleChangePasswordCancel"
            class="change-password-modal"
        >
            <div v-if="currentUser" class="change-password-content">
                <p class="user-info-text">
                    <strong>會員：</strong>
                    {{ currentUser.name }} ({{ currentUser.email }})
                </p>
                <a-divider />
                <a-form
                    ref="changePasswordFormRef"
                    :model="changePasswordFormState"
                    :rules="changePasswordFormRules"
                    layout="vertical"
                >
                    <a-form-item label="新密碼" name="newPassword">
                        <a-input-password
                            v-model:value="changePasswordFormState.newPassword"
                            placeholder="請輸入新密碼"
                            size="large"
                        />
                    </a-form-item>
                    <a-form-item label="確認新密碼" name="confirmPassword">
                        <a-input-password
                            v-model:value="
                                changePasswordFormState.confirmPassword
                            "
                            placeholder="請再次輸入新密碼"
                            size="large"
                        />
                    </a-form-item>
                </a-form>
            </div>
        </a-modal>

        <!-- 匯入任務常駐通知（右側）：可展開/收合，輪詢進度，成功/失敗後可確認關閉 -->
        <div
            v-if="activeImportTasks.length > 0"
            class="import-tasks-panel"
            :class="{ 'is-expanded': importTasksPanelExpanded }"
        >
            <div
                class="import-tasks-panel-header"
                @click="importTasksPanelExpanded = !importTasksPanelExpanded"
            >
                <span class="import-tasks-panel-title">
                    匯入進度 ({{ activeImportTasks.length }})
                </span>
                <DownOutlined
                    v-if="!importTasksPanelExpanded"
                    class="import-tasks-chevron"
                />
                <UpOutlined v-else class="import-tasks-chevron" />
            </div>
            <div v-show="importTasksPanelExpanded" class="import-tasks-list">
                <div
                    v-for="task in activeImportTasks"
                    :key="task.import_id"
                    class="import-task-card"
                    :class="{
                        'is-done':
                            task.status === 'completed' ||
                            task.status === 'failed'
                    }"
                >
                    <div class="import-task-title">
                        匯入 #{{ task.import_id }}
                    </div>
                    <div class="import-task-status">
                        {{ getImportStatusText(task.status) }}
                    </div>
                    <template v-if="task.status === 'completed'">
                        <div class="import-task-counts">
                            <span
                                v-if="(task.success_count ?? 0) > 0"
                                class="count success"
                            >
                                成功 {{ task.success_count }} 筆
                            </span>
                            <span
                                v-if="(task.failure_count ?? 0) > 0"
                                class="count fail"
                            >
                                失敗 {{ task.failure_count }} 筆
                            </span>
                        </div>
                        <div class="import-task-actions">
                            <a-button
                                size="small"
                                class="unified-secondary-btn import-task-btn import-task-detail"
                                @click.stop="showImportTaskDetail(task)"
                            >
                                查看詳情
                            </a-button>
                            <a-button
                                type="primary"
                                size="small"
                                class="unified-add-btn import-task-btn import-task-dismiss"
                                @click.stop="dismissImportTask(task.import_id)"
                            >
                                確認
                            </a-button>
                        </div>
                    </template>
                    <template v-else-if="task.status === 'failed'">
                        <div
                            v-if="task.error_message"
                            class="import-task-error"
                        >
                            {{ task.error_message }}
                        </div>
                        <div class="import-task-actions">
                            <a-button
                                size="small"
                                class="unified-secondary-btn import-task-btn import-task-detail"
                                @click.stop="showImportTaskDetail(task)"
                            >
                                查看詳情
                            </a-button>
                            <a-button
                                type="primary"
                                size="small"
                                class="unified-add-btn import-task-btn import-task-dismiss"
                                @click.stop="dismissImportTask(task.import_id)"
                            >
                                確認
                            </a-button>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
// MemberList Component - 會員列表頁面
import { ref, reactive, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import { message } from 'ant-design-vue';
import { useRoute, useRouter } from 'vue-router';
import { showErrorMessage, handleApiError } from '@/utils/messageHelper';
import {
    DownloadOutlined,
    InboxOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    ManOutlined,
    WomanOutlined,
    TableOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
    DownOutlined,
    UpOutlined
} from '@ant-design/icons-vue';
import {
    getUsers,
    getUserDetail,
    createUser,
    updateUserExp,
    exportUsers,
    importUsers,
    getImportStatus,
    resetUserPassword,
    downloadImportTemplate,
    getUserOrganizationChart
} from '@/api/users';
import { adjustUserPoints } from '@/api/points';
import { getLevels } from '@/api/levels';
import { fetchAllDepartmentRows, toDepartmentOptions } from '@/api/departments';
import {
    fetchAllRankRows,
    getUserRankHistory,
    toRankOptions
} from '@/api/ranks';
import { searchUsers } from '@/api/users';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { canAccessMemberDetail } from '@/constants/permissionRules';
import DataTable from '@/components/common/DataTable.vue';
import OrgChart from '@/components/common/OrgChart.vue';
import {
    PROFILE_CONFIG,
    MEMBER_LIST_PROFILE,
    getProfileFromRouteMeta,
    canChangePasswordForIdentity
} from '@/views/members/memberListProfiles.js';
import {
    getIdentityName,
    getIdentityTagColor
} from '@/constants/memberIdentity.js';
import {
    taiwanIdNumberError,
    TAIWAN_ID_LABEL
} from '@/utils/taiwanIdNumber.js';

// 資料狀態
const users = ref([]);
const loading = ref(false);
const searchText = ref('');
const levels = ref([]);
const departments = ref([]);
const ranks = ref([]);

// 篩選器狀態
const filters = reactive({
    department_id: undefined,
    referral_department_id: undefined,
    rank_id: undefined,
    status: undefined,
    identity: undefined,
    name: undefined,
    email: '',
    phone: '',
    id_number: '',
    has_line: undefined,
    referral_id: undefined,
    mentor_id: undefined,
    assigned_at_from: undefined,
    assigned_at_to: undefined,
    referral_code: undefined,
    deregistered: undefined
});

// 下拉選項
const departmentOptions = ref([]);
const rankOptions = ref([]);
const statusOptions = [
    { label: '啟用', value: 1 },
    { label: '停用', value: 0 }
];
const identityOptions = [
    { label: '遊客', value: 'guest' },
    { label: '學員', value: 'student' },
    { label: '夥伴', value: 'partner' },
    { label: '管理員', value: 'manager' }
];
const lineBindingOptions = [
    { label: '已綁定 LINE', value: 1 },
    { label: '未綁定 LINE', value: 0 }
];
const deregisteredOptions = [
    { label: '已除籍', value: 1 },
    { label: '未除籍', value: 0 }
];

// 搜尋選項
const nameOptions = ref([]);
const nameLoading = ref(false);
const referralOptions = ref([]);
const referralLoading = ref(false);
const mentorOptions = ref([]);
const mentorLoading = ref(false);
const recruiterOptions = ref([]);
const recruiterLoading = ref(false);

// 記錄夥伴表單搜尋關鍵字，便於自動選取
const lastPartnerReferralQuery = ref('');
const lastPartnerMentorQuery = ref('');
const lastPartnerRecruiterQuery = ref('');

let nameSearchTimer = null;
let referralSearchTimer = null;
let mentorSearchTimer = null;
let recruiterSearchTimer = null;
const USER_SEARCH_DEBOUNCE_MS = 250;

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0
});

// Modal 狀態
const detailModalVisible = ref(false);
const editModalVisible = ref(false);
const editModalLoading = ref(false);
const pointsModalVisible = ref(false);
const pointsModalLoading = ref(false);
const importModalVisible = ref(false);
const importModalLoading = ref(false);
const templateDownloadLoading = ref(false);
const importErrorsModalVisible = ref(false);
const importResult = ref({
    status: '',
    errorMessage: '',
    successCount: 0,
    createdCount: 0,
    updatedCount: 0,
    failureCount: 0,
    failures: [],
    missingData: []
});
const createModalVisible = ref(false);
const createModalLoading = ref(false);
const rankHistoryModalVisible = ref(false);
const rankHistoryLoading = ref(false);
const accountPasswordModalVisible = ref(false);
const createdAccountInfo = ref(null);
const changePasswordModalVisible = ref(false);
const changePasswordModalLoading = ref(false);
const columnSettingsModalVisible = ref(false);

const userDetail = ref(null);
const currentUser = ref(null);
const userRankHistory = ref([]);
const editFormRef = ref(null);
const pointsFormRef = ref(null);
const createFormRef = ref(null);
const changePasswordFormRef = ref(null);
const importFileList = ref([]);

// 非同步匯入任務：存在本地，常駐通知 + 輪詢
const IMPORT_TASKS_STORAGE_KEY = 'member_import_tasks';
const IMPORT_POLL_INTERVAL_MS = 30000; // 30 秒輪詢
// 檔案小於此 bytes 用同步匯入，否則用非同步（50KB）
const IMPORT_ASYNC_THRESHOLD_BYTES = 50 * 1024;
const activeImportTasks = ref([]);
const importPollTimers = ref({}); // { [import_id]: timerId }
const importTasksPanelExpanded = ref(false); // 匯入進度區塊：收合 / 展開

const loadImportTasksFromStorage = () => {
    try {
        const raw = localStorage.getItem(IMPORT_TASKS_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const saveImportTasksToStorage = () => {
    try {
        localStorage.setItem(
            IMPORT_TASKS_STORAGE_KEY,
            JSON.stringify(activeImportTasks.value)
        );
    } catch (e) {
        console.warn('儲存匯入任務到本地失敗', e);
    }
};

const getImportStatusText = (status) => {
    const map = {
        pending: '已排入佇列，等待執行',
        processing: '匯入中…',
        completed: '匯入完成',
        failed: '匯入失敗'
    };
    return map[status] || status || '未知';
};

const stopPolling = (importId) => {
    const id = importPollTimers.value[importId];
    if (id != null) {
        clearInterval(id);
        delete importPollTimers.value[importId];
    }
};

const pollImportStatus = (importId) => {
    const doPoll = async () => {
        try {
            const res = await getImportStatus(importId);
            const data = (res && res.data) || {};
            const status = data.status || 'pending';
            const task = activeImportTasks.value.find(
                (t) => t.import_id === importId
            );
            if (!task) return;
            task.status = status;
            task.success_count = data.success_count;
            task.created_count = data.created_count;
            task.updated_count = data.updated_count;
            task.failure_count = data.failure_count;
            task.failures = data.failures || [];
            task.missing_data = data.missing_data || [];
            task.error_message = data.error_message;
            task.original_filename = data.original_filename;
            task.total_rows = data.total_rows;
            task.processed_rows = data.processed_rows;
            task.started_at = data.started_at;
            task.finished_at = data.finished_at;
            saveImportTasksToStorage();
            if (status === 'completed' || status === 'failed') {
                stopPolling(importId);
            }
        } catch {
            // 輪詢失敗不中斷，下次再試
        }
    };
    doPoll();
    const timerId = setInterval(doPoll, IMPORT_POLL_INTERVAL_MS);
    importPollTimers.value[importId] = timerId;
};

const addImportTask = (importId) => {
    const task = {
        import_id: importId,
        status: 'pending',
        success_count: null,
        failure_count: null,
        error_message: null
    };
    activeImportTasks.value.push(task);
    saveImportTasksToStorage();
    pollImportStatus(importId);
};

const dismissImportTask = (importId) => {
    const task = activeImportTasks.value.find((t) => t.import_id === importId);
    stopPolling(importId);
    activeImportTasks.value = activeImportTasks.value.filter(
        (t) => t.import_id !== importId
    );
    saveImportTasksToStorage();
    if (task && task.status === 'completed' && (task.success_count || 0) > 0) {
        loadUsers();
    }
};

// 查看匯入任務詳情：點擊時重新打 API，保證看到最新結果
const showImportTaskDetail = async (task) => {
    if (!task) return;
    try {
        const res = await getImportStatus(task.import_id);
        const data = (res && res.data) || {};
        importResult.value = {
            status: data.status || task.status || '',
            errorMessage: data.error_message || task.error_message || '',
            successCount: data.success_count || 0,
            createdCount: data.created_count || 0,
            updatedCount: data.updated_count || 0,
            failureCount: data.failure_count || 0,
            failures: data.failures || [],
            missingData: data.missing_data || []
        };
        importErrorsModalVisible.value = true;
    } catch (error) {
        console.error('取得匯入詳情失敗:', error);
        showErrorMessage('取得匯入詳情失敗，請稍後再試');
    }
};

// 身分證顯示狀態
const idNumberVisible = ref({});

// 路由
const route = useRoute();
const router = useRouter();

// 權限管理
const adminStore = useAdminInfo();

const listProfile = computed(() => getProfileFromRouteMeta(route.meta));
const profileConfig = computed(() => PROFILE_CONFIG[listProfile.value]);
const pageTitle = computed(() => route.meta?.title || profileConfig.value.label);
const pageSubtitle = computed(() => {
    if (listProfile.value === MEMBER_LIST_PROFILE.BUSINESS) {
        return '可用姓名／手機／Email 查詢；並可依推薦／輔導事業部、組織與聘階篩選';
    }
    if (listProfile.value === MEMBER_LIST_PROFILE.GENERAL_ADMIN) {
        return '可用姓名／手機／Email 查詢；並可查看基本聯絡與推薦資訊';
    }
    return '可用姓名／手機／Email 查詢，管理系統中的所有會員資料';
});

const filterVisible = (key) => {
    const cfg = profileConfig.value;
    if (!cfg.filters) {
        return true;
    }
    return cfg.filters.includes(key);
};

const profileActionVisible = (action) => {
    const cfg = profileConfig.value;
    return (cfg.actions || []).includes(action);
};

const canShowPasswordAction = (record) =>
    canChangePasswordForIdentity(listProfile.value, record?.identity);

const useFuzzyNameFilter = computed(
    () => listProfile.value !== MEMBER_LIST_PROFILE.FULL
);

// 權限檢查（API／按鈕能力；版面由路由 profile 決定）
const hasCreatePermission = computed(
    () =>
        adminStore.isFullAccessAdmin ||
        adminStore.hasPermissionCode('100101')
);
const hasImportPermission = computed(
    () =>
        adminStore.isFullAccessAdmin ||
        adminStore.hasPermissionCode('100104')
);
/** @deprecated 改以 profile.allowCreate + hasCreatePermission；保留給其他邏輯相容 */
const canCreate = computed(
    () => profileConfig.value.allowCreate && hasCreatePermission.value
);
const canEdit = computed(
    () =>
        adminStore.isFullAccessAdmin ||
        adminStore.hasPermissionCode('100102')
);
const canDelete = computed(
    () =>
        adminStore.isFullAccessAdmin ||
        adminStore.hasPermissionCode('100103')
);
const canImport = computed(
    () => profileConfig.value.allowImport && hasImportPermission.value
);
const canExport = computed(
    () =>
        adminStore.isFullAccessAdmin ||
        adminStore.hasPermissionCode('100105')
);
const canViewOrgChart = computed(
    () =>
        adminStore.hasPermissionCode('100118') ||
        adminStore.hasPermissionCode('100114')
);
const canViewDetail = computed(() =>
    canAccessMemberDetail(adminStore.permissions)
); // 會員詳情（含子分頁碼）

// 篩選器摺疊狀態
const filterActiveKeys = ref([]); // 預設收合

// 切換篩選面板
const toggleFilterPanel = () => {
    if (filterActiveKeys.value.length > 0) {
        filterActiveKeys.value = [];
    } else {
        filterActiveKeys.value = ['1'];
    }
};

// 表單資料
const editFormState = reactive({
    exp: 0
});

const pointsFormState = reactive({
    point_type: undefined,
    type: 'add',
    points: 0,
    reason: ''
});

const createFormState = reactive({
    name: '',
    email: '',
    gender: undefined,
    phone: undefined,
    birthdate: undefined,
    id_number: undefined,
    line_id: undefined,
    identity: undefined,
    status: true,
    department_id: undefined,
    partner: {
        department_id: undefined,
        rank_id: undefined,
        referral_id: undefined,
        mentor_id: undefined,
        recruiter_id: undefined,
        referral_department_id: undefined,
        status: true,
        assigned_at: undefined
    }
});

const changePasswordFormState = reactive({
    newPassword: '',
    confirmPassword: ''
});

// 表單驗證規則
const editFormRules = {
    exp: [
        { required: true, message: '請輸入經驗值', trigger: 'blur' },
        { type: 'number', min: 0, message: '經驗值不能小於 0', trigger: 'blur' }
    ]
};

const pointsFormRules = {
    point_type: [
        { required: true, message: '請選擇積分類型', trigger: 'change' }
    ],
    type: [{ required: true, message: '請選擇調整類型', trigger: 'change' }],
    points: [
        { required: true, message: '請輸入積分數量', trigger: 'blur' },
        { type: 'number', min: 1, message: '積分數量至少為 1', trigger: 'blur' }
    ],
    reason: [{ required: true, message: '請輸入調整原因', trigger: 'blur' }]
};

// 台灣身分證／居留證統一證號格式驗證（對齊 API TaiwanIdNumber）
const validateIdNumber = (rule, value) => {
    const err = taiwanIdNumberError(value);
    if (err) return Promise.reject(err);
    return Promise.resolve();
};

// 動態表單驗證規則（根據身份調整夥伴欄位是否必填）
const createFormRules = computed(() => {
    const isPartner = createFormState.identity === 'partner';

    const rules = {
        name: [{ required: true, message: '請輸入姓名', trigger: 'blur' }],
        email: [
            { required: true, message: '請輸入 Email', trigger: 'blur' },
            { type: 'email', message: 'Email 格式不正確', trigger: 'blur' }
        ],
        gender: [{ required: true, message: '請選擇性別', trigger: 'change' }],
        identity: [
            { required: true, message: '請選擇會員身分', trigger: 'change' }
        ],
        phone: [
            {
                pattern: /^09\d{8}$/,
                message: '請輸入有效的手機號碼（09xxxxxxxx）',
                trigger: 'blur'
            }
        ],
        id_number: [{ validator: validateIdNumber, trigger: 'blur' }]
    };

    return rules;
});

const changePasswordFormRules = {
    newPassword: [
        { required: true, message: '請輸入新密碼', trigger: 'blur' },
        { min: 6, message: '密碼長度至少 6 位', trigger: 'blur' }
    ],
    confirmPassword: [
        {
            validator: (rule, value) => {
                if (!value) {
                    return Promise.reject('請確認新密碼');
                }
                if (value !== changePasswordFormState.newPassword) {
                    return Promise.reject('兩次輸入的密碼不一致');
                }
                return Promise.resolve();
            },
            trigger: 'blur'
        }
    ]
};

// 所有表格欄位配置（完整列表）
const allColumns = [
    {
        title: '推薦事業部',
        key: 'referral_department',
        width: 120,
        align: 'center',
        fixed: 'left'
    },
    {
        title: '輔導事業部',
        key: 'department',
        width: 120,
        align: 'center',
        fixed: 'left'
    },
    {
        title: '姓名',
        key: 'name',
        width: 120,
        align: 'center',
        fixed: 'left'
    },
    {
        title: 'Email',
        key: 'email',
        width: 180,
        align: 'center'
    },
    {
        title: '電話',
        key: 'phone',
        width: 130,
        align: 'center'
    },
    {
        title: '生日',
        key: 'birthdate',
        width: 120,
        align: 'center'
    },
    {
        title: TAIWAN_ID_LABEL,
        key: 'id_number',
        width: 150,
        align: 'center'
    },
    {
        title: '身份',
        key: 'identity',
        width: 120,
        align: 'center'
    },
    {
        title: '推薦人',
        key: 'referral',
        width: 120,
        align: 'center'
    },
    {
        title: '輔導人',
        key: 'mentor',
        width: 120,
        align: 'center'
    },
    {
        title: '起聘日期',
        key: 'partner_assigned_at',
        width: 120,
        align: 'center'
    },
    {
        title: '狀態',
        key: 'status',
        width: 80,
        align: 'center'
    },
    {
        title: '註冊時間',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        align: 'center'
    },
    {
        title: '操作',
        key: 'action',
        width: 180,
        align: 'center'
    }
];

// 欄位顯示設置 - 從 localStorage 讀取，預設全部顯示
const STORAGE_KEY = 'member_list_visible_columns';
const getDefaultVisibleColumns = () => allColumns.map((col) => col.key);
const visibleColumns = ref(getDefaultVisibleColumns());

// 載入保存的欄位設置
const loadColumnSettings = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const savedColumns = JSON.parse(saved);
            // 驗證保存的欄位是否還存在於 allColumns 中
            const validColumns = savedColumns.filter((key) =>
                allColumns.some((col) => col.key === key)
            );
            if (validColumns.length > 0) {
                visibleColumns.value = validColumns;
                // 確保「操作」欄位始終包含在內
                if (!visibleColumns.value.includes('action')) {
                    visibleColumns.value.push('action');
                }
            }
        } else {
            // 如果沒有保存的設置，確保「操作」欄位包含在預設值中
            if (!visibleColumns.value.includes('action')) {
                visibleColumns.value.push('action');
            }
        }
    } catch (error) {
        console.error('載入欄位設置失敗:', error);
        // 發生錯誤時，確保「操作」欄位包含在內
        if (!visibleColumns.value.includes('action')) {
            visibleColumns.value.push('action');
        }
    }
};

// 保存欄位設置到 localStorage
const saveColumnSettings = () => {
    try {
        // 確保「操作」欄位始終包含在內
        if (!visibleColumns.value.includes('action')) {
            visibleColumns.value.push('action');
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns.value));
        columnSettingsModalVisible.value = false;
        message.success('欄位設置已保存');
    } catch (error) {
        console.error('保存欄位設置失敗:', error);
        showErrorMessage('保存欄位設置失敗');
    }
};

// 顯示欄位設置 Modal
const showColumnSettingsModal = () => {
    columnSettingsModalVisible.value = true;
};

// 表格欄位配置 - 依 profile 或 visibleColumns 過濾
const columns = computed(() => {
    const cfg = profileConfig.value;
    if (cfg.columns) {
        return allColumns.filter((col) => cfg.columns.includes(col.key));
    }
    return allColumns.filter((col) => visibleColumns.value.includes(col.key));
});

// 聘階歷史表格欄位配置
const rankHistoryColumns = [
    {
        title: '原聘階',
        key: 'from_rank',
        width: 120,
        align: 'center'
    },
    {
        title: '新聘階',
        key: 'to_rank',
        width: 120,
        align: 'center'
    },
    {
        title: '變更原因',
        key: 'reason',
        ellipsis: true
    },
    {
        title: '變更時間',
        key: 'created_at',
        width: 180,
        align: 'center'
    }
];

// 載入會員列表
const loadUsers = async () => {
    try {
        loading.value = true;

        // 構建篩選參數
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize
        };

        // 添加篩選條件
        if (filters.department_id) params.department_id = filters.department_id;
        if (filters.referral_department_id) {
            params.referral_department_id = filters.referral_department_id;
        }
        if (filters.rank_id) params.rank_id = filters.rank_id;
        if (filters.status !== undefined) params.status = filters.status;
        if (filters.identity) params.identity = filters.identity;
        if (filters.name) {
            if (useFuzzyNameFilter.value) {
                params.name = filters.name;
            } else {
                params.id = filters.name;
            }
        }
        if (filters.email) params.email = filters.email;
        if (filters.phone) params.phone = filters.phone;
        if (filters.id_number) params.id_number = filters.id_number;
        if (filters.has_line !== undefined) params.has_line = filters.has_line;
        if (filters.referral_id) params.referral_id = filters.referral_id;
        if (filters.mentor_id) params.mentor_id = filters.mentor_id;
        if (filters.assigned_at_from) {
            params.assigned_at_from = filters.assigned_at_from;
        }
        if (filters.assigned_at_to) {
            params.assigned_at_to = filters.assigned_at_to;
        }
        if (filters.referral_code) params.referral_code = filters.referral_code;
        if (filters.deregistered !== undefined) params.deregistered = filters.deregistered;

        const response = await getUsers(params);

        if (response && response.result) {
            // API 回應格式：response.data.data 是資料，response.data.pagination 是分頁資訊
            users.value = response.data?.data || [];
            pagination.total = response.data?.pagination?.total || 0;
            pagination.current = response.data?.pagination?.current_page || 1;
        } else {
            console.warn('會員列表回應異常:', response);
            users.value = [];
            pagination.total = 0;
        }
    } catch (error) {
        console.error('載入會員列表失敗:', error);
        showErrorMessage('載入會員列表失敗: ' + (error.message || '未知錯誤'));
        users.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

// 載入等級列表
const loadLevels = async () => {
    try {
        const response = await getLevels();

        if (response && response.result) {
            // 檢查 response.data 是否為陣列，如果有分頁結構則取 data.data
            if (Array.isArray(response.data)) {
                levels.value = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
                levels.value = response.data.data;
            } else {
                console.warn('等級資料格式異常:', response);
                levels.value = [];
            }
        } else {
            levels.value = [];
        }
    } catch (error) {
        console.error('載入等級列表失敗:', error);
        levels.value = [];
    }
};

// 載入部門列表
const loadDepartments = async () => {
    try {
        departments.value = await fetchAllDepartmentRows();
    } catch (error) {
        console.error('載入部門列表失敗:', error);
        departments.value = [];
    }
};

// 載入職級列表
const loadRanks = async () => {
    try {
        ranks.value = await fetchAllRankRows();
    } catch (error) {
        console.error('載入職級列表失敗:', error);
        ranks.value = [];
    }
};

// 獲取等級名稱
const getLevelName = (levelId) => {
    const level = levels.value.find((l) => l.id === levelId);
    return level ? level.name : '未設定';
};

// 獲取等級顏色
const getLevelColor = (levelId) => {
    const colors = ['#87d068', '#2db7f5', '#722ed1', '#f50', '#faad14'];
    return colors[levelId % colors.length];
};

// 獲取部門名稱
const getDepartmentName = (departmentId) => {
    if (!departmentId) return '';
    if (!Array.isArray(departments.value) || departments.value.length === 0)
        return '載入中...';
    const department = departments.value.find((d) => d.id === departmentId);
    return department ? department.name : '';
};

// 獲取職級名稱
const getRankName = (rankId) => {
    if (!rankId) return '';
    if (!Array.isArray(ranks.value) || ranks.value.length === 0)
        return '載入中...';
    const rank = ranks.value.find((r) => r.id === rankId);
    return rank ? rank.name : '';
};

// 獲取聘階顏色（用於夥伴身份標籤）
const getRankColor = (rankId) => {
    if (!rankId || !Array.isArray(ranks.value) || ranks.value.length === 0)
        return 'blue';
    const rank = ranks.value.find((r) => r.id === rankId);
    return rank && rank.color ? rank.color : 'blue';
};

const parseHexColor = (color) => {
    if (!color || typeof color !== 'string') return null;
    const hex = color.replace(/^#/, '');
    if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hex)) return null;
    if (hex.length === 3) {
        return {
            r: parseInt(hex[0] + hex[0], 16),
            g: parseInt(hex[1] + hex[1], 16),
            b: parseInt(hex[2] + hex[2], 16)
        };
    }
    return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16)
    };
};

const getRankColorLuminance = (color) => {
    const rgb = parseHexColor(color);
    if (!rgb) return null;
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
};

// 淡色背景改用深色字，避免 Ant Design 預設白字看不清
const isRankColorLight = (color) => {
    const luminance = getRankColorLuminance(color);
    return luminance !== null && luminance >= 0.62;
};

// 近白背景在白底表格幾乎隱形，額外加深邊框／底色
const isRankColorNearWhite = (color) => {
    const luminance = getRankColorLuminance(color);
    return luminance !== null && luminance >= 0.9;
};

// 獲取身份名稱（見 @/constants/memberIdentity）
// getIdentityName / getIdentityTagColor 已由 constants 匯入

// 搜尋處理
const handleSearch = () => {
    pagination.current = 1;
    loadUsers();
};

// 重置篩選器
const handleReset = () => {
    filters.department_id = undefined;
    filters.referral_department_id = undefined;
    filters.rank_id = undefined;
    filters.status = undefined;
    filters.identity = undefined;
    filters.name = undefined;
    filters.email = '';
    filters.phone = '';
    filters.id_number = '';
    filters.has_line = undefined;
    filters.referral_id = undefined;
    filters.mentor_id = undefined;
    filters.assigned_at_from = undefined;
    filters.assigned_at_to = undefined;
    filters.referral_code = undefined;
    filters.deregistered = undefined;
    pagination.current = 1;
    loadUsers();
};

// 載入部門選項
const loadDepartmentOptions = async () => {
    try {
        const rows = await fetchAllDepartmentRows();
        departmentOptions.value = toDepartmentOptions(rows);
    } catch (error) {
        console.error('載入部門選項失敗:', error);
    }
};

// 載入等級選項
const loadRankOptions = async () => {
    try {
        const rows = await fetchAllRankRows();
        rankOptions.value = toRankOptions(rows);
    } catch (error) {
        console.error('載入等級選項失敗:', error);
    }
};

// 泛用化：依關鍵字載入使用者選項
const fetchUserOptions = async (keyword, targetOptionsRef, loadingRef) => {
    const kw = keyword && keyword.trim();
    if (!kw || kw.length < 2) {
        targetOptionsRef.value = [];
        return;
    }
    try {
        loadingRef.value = true;
        const res = await searchUsers({ name: kw, limit: 10 });
        if (res?.result) {
            const arr = Array.isArray(res.data)
                ? res.data
                : res.data?.data || [];
            targetOptionsRef.value = arr.map((u) => ({
                label: u.name || `#${u.id}`,
                value: u.id
            }));
        }
    } catch (e) {
        console.error('載入選項失敗:', e);
    } finally {
        loadingRef.value = false;
    }
};

// 搜尋姓名
const onNameSearch = (val) => {
    clearTimeout(nameSearchTimer);
    nameSearchTimer = setTimeout(
        () => fetchUserOptions(val, nameOptions, nameLoading),
        USER_SEARCH_DEBOUNCE_MS
    );
};

const onNameClear = () => {
    nameOptions.value = [];
    filters.name = undefined;
};

// 搜尋推薦人
const onReferralSearch = (val) => {
    clearTimeout(referralSearchTimer);
    referralSearchTimer = setTimeout(
        () => fetchUserOptions(val, referralOptions, referralLoading),
        USER_SEARCH_DEBOUNCE_MS
    );
};

const onReferralClear = () => {
    referralOptions.value = [];
    filters.referral_id = undefined;
};

// 搜尋輔導人
const onMentorSearch = (val) => {
    clearTimeout(mentorSearchTimer);
    mentorSearchTimer = setTimeout(
        () => fetchUserOptions(val, mentorOptions, mentorLoading),
        USER_SEARCH_DEBOUNCE_MS
    );
};

const onMentorClear = () => {
    mentorOptions.value = [];
    filters.mentor_id = undefined;
};

const onRecruiterSearch = (val) => {
    clearTimeout(recruiterSearchTimer);
    recruiterSearchTimer = setTimeout(
        () => fetchUserOptions(val, recruiterOptions, recruiterLoading),
        USER_SEARCH_DEBOUNCE_MS
    );
};

// 新增會員表單中搜尋推薦人/輔導人/招募人
const onPartnerReferralSearch = (val) => {
    lastPartnerReferralQuery.value = val || '';
    onReferralSearch(val);
};
const onPartnerMentorSearch = (val) => {
    lastPartnerMentorQuery.value = val || '';
    onMentorSearch(val);
};
const onPartnerRecruiterSearch = (val) => {
    lastPartnerRecruiterQuery.value = val || '';
    onRecruiterSearch(val);
};

/** 將推薦人選項同步進招募人選項，並複製 ID */
const copyCreateReferralToRecruiter = () => {
    const referralId = createFormState.partner.referral_id;
    if (!referralId) {
        message.warning('請先選擇推薦人');
        return;
    }
    const opt = referralOptions.value.find(
        (o) => Number(o.value) === Number(referralId)
    );
    if (opt && !recruiterOptions.value.some((o) => Number(o.value) === Number(referralId))) {
        recruiterOptions.value = [opt, ...recruiterOptions.value];
    }
    createFormState.partner.recruiter_id = referralId;
    message.success('已複製為推薦人');
};

const copyCreateReferralDeptFromReferrer = async () => {
    const referralId = createFormState.partner.referral_id;
    if (!referralId) {
        message.warning('請先選擇推薦人');
        return;
    }
    try {
        const res = await getUserDetail(referralId);
        const data = res?.data || {};
        const deptId =
            data.referral_department_id ??
            data.partner?.referral_department_id ??
            data.department_id ??
            data.partner?.department_id ??
            null;
        if (!deptId) {
            message.warning('該推薦人尚未設定推薦事業部');
            return;
        }
        createFormState.partner.referral_department_id = deptId;
        message.success('已帶入推薦人的推薦事業部');
    } catch (e) {
        console.error(e);
        showErrorMessage('取得推薦人資料失敗');
    }
};

// 處理身分證字號輸入，自動轉為大寫
const handleIdNumberInput = (e) => {
    if (e && e.target) {
        createFormState.id_number = e.target.value.toUpperCase();
    }
};

// 複製到剪貼簿
const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        message.success('已複製到剪貼簿');
    } catch (error) {
        console.error('複製失敗:', error);
        // 降級方案：使用傳統方法
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            message.success('已複製到剪貼簿');
        } catch (err) {
            showErrorMessage('複製失敗，請手動複製');
        }
        document.body.removeChild(textArea);
    }
};

// 表格變更處理
const handleTableChange = ({ pagination: paginationInfo }) => {
    pagination.current = paginationInfo.current;
    pagination.pageSize = paginationInfo.pageSize;
    loadUsers();
};

// 依列表點擊套用推薦人篩選
const setReferralFilter = async (record) => {
    if (!record) return;
    let id = Number(
        record.referral_id ?? record.referralId ?? record.referral_user_id
    );
    let label = record.referral;
    if (!id && label) {
        try {
            const res = await searchUsers({
                name: String(label).trim(),
                limit: 1
            });
            const arr = Array.isArray(res?.data)
                ? res.data
                : res?.data?.data || [];
            if (arr.length > 0) {
                id = Number(arr[0].id);
                label = arr[0].name || label;
            }
        } catch (e) {}
    }
    if (!id) return;
    if (!label) label = `#${id}`;
    if (!referralOptions.value.some((o) => Number(o.value) === id)) {
        referralOptions.value = [
            { label, value: id },
            ...referralOptions.value
        ];
    }
    filters.referral_id = id;
    filterActiveKeys.value = ['1'];
    pagination.current = 1;
    await loadUsers();
};

// 依列表點擊套用輔導人篩選
const setMentorFilter = async (record) => {
    if (!record) return;
    let id = Number(
        record.mentor_id ?? record.mentorId ?? record.mentor_user_id
    );
    let label = record.mentor;
    if (!id && label) {
        try {
            const res = await searchUsers({
                name: String(label).trim(),
                limit: 1
            });
            const arr = Array.isArray(res?.data)
                ? res.data
                : res?.data?.data || [];
            if (arr.length > 0) {
                id = Number(arr[0].id);
                label = arr[0].name || label;
            }
        } catch (e) {}
    }
    if (!id) return;
    if (!label) label = `#${id}`;
    if (!mentorOptions.value.some((o) => Number(o.value) === id)) {
        mentorOptions.value = [{ label, value: id }, ...mentorOptions.value];
    }
    filters.mentor_id = id;
    filterActiveKeys.value = ['1'];
    pagination.current = 1;
    await loadUsers();
};

// 檢視詳情
const viewDetail = (record) => {
    if (!canAccessMemberDetail(adminStore.permissions)) {
        message.warning('您沒有查看會員詳情的權限');
        return;
    }
    router.push({
        path: `/members/${record.id}`,
        query: { from: route.name }
    });
};

// 編輯會員
const editUser = (record) => {
    currentUser.value = record;
    editFormState.exp = record.exp || 0;
    editModalVisible.value = true;
};

// 編輯確認
const handleEditOk = async () => {
    try {
        await editFormRef.value.validateFields();
        editModalLoading.value = true;

        const response = await updateUserExp(currentUser.value.id, {
            exp: editFormState.exp
        });

        if (response.result) {
            message.success('更新成功');
            editModalVisible.value = false;
            loadUsers();
        } else {
            showErrorMessage(response.message || '更新失敗');
        }
    } catch (error) {
        console.error('更新失敗:', error);
    } finally {
        editModalLoading.value = false;
    }
};

// 編輯取消
const handleEditCancel = () => {
    editFormRef.value.resetFields();
    currentUser.value = null;
};

// 積分管理
const managePoints = (record) => {
    currentUser.value = record;
    pointsFormState.point_type = undefined;
    pointsFormState.type = 'add';
    pointsFormState.points = 0;
    pointsFormState.reason = '';
    pointsModalVisible.value = true;
};

// 積分調整確認
const handlePointsOk = async () => {
    try {
        // 驗證表單
        await pointsFormRef.value.validate();

        pointsModalLoading.value = true;

        // 根據調整類型決定 change_amount 的正負
        const changeAmount =
            pointsFormState.type === 'add'
                ? pointsFormState.points
                : -pointsFormState.points;

        const response = await adjustUserPoints(currentUser.value.id, {
            point_type: pointsFormState.point_type,
            change_amount: changeAmount,
            reason: pointsFormState.reason
        });

        if (response.result) {
            message.success('積分調整成功');
            pointsModalVisible.value = false;
            loadUsers();
        } else {
            showErrorMessage(response.message || '積分調整失敗');
        }
    } catch (error) {
        console.error('積分調整失敗:', error);
        showErrorMessage('積分調整失敗');
    } finally {
        pointsModalLoading.value = false;
    }
};

// 積分調整取消
const handlePointsCancel = () => {
    pointsFormRef.value.resetFields();
    currentUser.value = null;
};

// 顯示新增會員 Modal
const onCreateClick = () => {
    if (!hasCreatePermission.value) {
        message.warning('請在角色權限勾選「新增會員」');
        return;
    }
    showCreateModal();
};

const onImportClick = () => {
    if (!hasImportPermission.value) {
        message.warning('請在角色權限勾選「匯入會員資料」');
        return;
    }
    showImportModal();
};

const showCreateModal = () => {
    createFormState.name = '';
    createFormState.email = '';
    createFormState.gender = undefined;
    createFormState.phone = undefined;
    createFormState.birthdate = undefined;
    createFormState.id_number = undefined;
    createFormState.line_id = undefined;
    createFormState.identity = undefined;
    createFormState.status = true;
    createFormState.department_id = undefined;
    createFormState.partner.department_id = undefined;
    createFormState.partner.rank_id = undefined;
    createFormState.partner.referral_id = undefined;
    createFormState.partner.mentor_id = undefined;
    createFormState.partner.recruiter_id = undefined;
    createFormState.partner.referral_department_id = undefined;
    createFormState.partner.status = true;
    createFormState.partner.assigned_at = undefined;
    lastPartnerRecruiterQuery.value = '';
    recruiterOptions.value = [];
    createModalVisible.value = true;
};

// 監聽身份變化，自動清理不相關欄位
watch(
    () => createFormState.identity,
    (newIdentity, oldIdentity) => {
        if (newIdentity !== oldIdentity && createModalVisible.value) {
            // 切換身份時清理相關欄位
            if (newIdentity === 'guest') {
                // guest 不可帶 rank_id、mentor_id；輔導事業部也不顯示
                createFormState.department_id = undefined;
                createFormState.partner.department_id = undefined;
                createFormState.partner.rank_id = undefined;
                createFormState.partner.mentor_id = undefined;
            } else if (newIdentity === 'student') {
                // student 不可帶 rank_id、mentor_id
                createFormState.partner.rank_id = undefined;
                createFormState.partner.mentor_id = undefined;
                // 相容舊欄位：頂層 department_id 併入 partner
                if (
                    createFormState.department_id &&
                    !createFormState.partner.department_id
                ) {
                    createFormState.partner.department_id =
                        createFormState.department_id;
                }
                createFormState.department_id = undefined;
            } else if (newIdentity === 'partner') {
                // partner 的部門應該在 partner.department_id
                if (createFormState.department_id) {
                    createFormState.partner.department_id =
                        createFormState.department_id;
                    createFormState.department_id = undefined;
                }
                // 預設夥伴狀態為啟用
                if (typeof createFormState.partner.status !== 'boolean') {
                    createFormState.partner.status = true;
                }
            }
        }
    }
);

// 新增會員確認
const handleCreateOk = async () => {
    try {
        // 送出前的自動補選：若為夥伴／學員／遊客，且推薦人/輔導人/招募人尚未選取，但搜尋結果中有與最後輸入完全相符的選項，則自動選取
        if (
            createFormState.identity === 'partner' ||
            createFormState.identity === 'student' ||
            createFormState.identity === 'guest'
        ) {
            if (
                !createFormState.partner.referral_id &&
                lastPartnerReferralQuery.value &&
                Array.isArray(referralOptions.value)
            ) {
                const hit = referralOptions.value.find(
                    (o) =>
                        String(o.label).trim() ===
                        String(lastPartnerReferralQuery.value).trim()
                );
                if (hit) createFormState.partner.referral_id = hit.value;
            }
            if (
                !createFormState.partner.mentor_id &&
                lastPartnerMentorQuery.value &&
                Array.isArray(mentorOptions.value)
            ) {
                const hit = mentorOptions.value.find(
                    (o) =>
                        String(o.label).trim() ===
                        String(lastPartnerMentorQuery.value).trim()
                );
                if (hit) createFormState.partner.mentor_id = hit.value;
            }
            if (
                !createFormState.partner.recruiter_id &&
                lastPartnerRecruiterQuery.value &&
                Array.isArray(recruiterOptions.value)
            ) {
                const hit = recruiterOptions.value.find(
                    (o) =>
                        String(o.label).trim() ===
                        String(lastPartnerRecruiterQuery.value).trim()
                );
                if (hit) createFormState.partner.recruiter_id = hit.value;
            }
        }

        // 直接依表單規則驗證（含夥伴身份時的動態必填）
        await createFormRef.value.validate();

        // 構建提交資料
        const submitData = {
            name: createFormState.name,
            email: createFormState.email,
            gender: createFormState.gender
        };

        // 非必填欄位
        if (createFormState.phone) submitData.phone = createFormState.phone;
        if (createFormState.birthdate)
            submitData.birthdate = createFormState.birthdate;
        if (createFormState.id_number)
            submitData.id_number = createFormState.id_number.toUpperCase();
        if (createFormState.line_id)
            submitData.line_id = createFormState.line_id;
        if (createFormState.identity)
            submitData.identity = createFormState.identity;
        if (createFormState.status !== undefined)
            submitData.status = createFormState.status;

        // 根據 identity 決定要提交的欄位
        const identity = createFormState.identity;

        if (identity === 'partner') {
            // partner 可填寫所有欄位
            const partnerData = {};
            if (createFormState.partner.department_id) {
                partnerData.department_id =
                    createFormState.partner.department_id;
            }
            if (createFormState.partner.rank_id) {
                partnerData.rank_id = createFormState.partner.rank_id;
            }
            if (createFormState.partner.referral_id) {
                partnerData.referral_id = createFormState.partner.referral_id;
            }
            if (createFormState.partner.mentor_id) {
                partnerData.mentor_id = createFormState.partner.mentor_id;
            }
            if (createFormState.partner.recruiter_id) {
                partnerData.recruiter_id =
                    createFormState.partner.recruiter_id;
            }
            if (createFormState.partner.referral_department_id) {
                partnerData.referral_department_id =
                    createFormState.partner.referral_department_id;
            }
            if (createFormState.partner.status !== undefined) {
                partnerData.status = createFormState.partner.status;
            }
            if (createFormState.partner.assigned_at)
                partnerData.assigned_at = createFormState.partner.assigned_at;

            // 只有至少有一個欄位有值時才加入 partner
            if (Object.keys(partnerData).length > 0) {
                submitData.partner = partnerData;
            }
        } else if (identity === 'student' || identity === 'guest') {
            const partnerData = {};
            if (
                identity === 'student' &&
                createFormState.partner.department_id
            ) {
                partnerData.department_id =
                    createFormState.partner.department_id;
            }
            if (createFormState.partner.referral_id) {
                partnerData.referral_id = createFormState.partner.referral_id;
            }
            if (createFormState.partner.recruiter_id) {
                partnerData.recruiter_id =
                    createFormState.partner.recruiter_id;
            }
            if (createFormState.partner.referral_department_id) {
                partnerData.referral_department_id =
                    createFormState.partner.referral_department_id;
            }
            if (Object.keys(partnerData).length > 0) {
                submitData.partner = partnerData;
            }
        }
        // manager 不需要額外欄位

        createModalLoading.value = true;
        const response = await createUser(submitData);

        if (response.result) {
            // 儲存帳號密碼資訊
            createdAccountInfo.value = {
                email: response.data.email,
                password: response.data.password
            };

            // 關閉新增會員 Modal
            createModalVisible.value = false;
            createFormRef.value.resetFields();

            // 開啟顯示帳號密碼 Modal
            accountPasswordModalVisible.value = true;

            // 重新載入會員列表
            loadUsers();
        } else {
            showErrorMessage(response.message || '新增會員失敗');
        }
    } catch (error) {
        console.error('新增會員失敗:', error);
        // 使用統一的錯誤處理函數，自動處理驗證錯誤
        handleApiError(error, '新增會員失敗', {
            name: '姓名',
            email: 'Email',
            phone: '電話',
            id_number: '身份證號碼',
            gender: '性別',
            birthdate: '生日',
            line_id: 'LINE ID',
            identity: '身份',
            status: '狀態',
            department_id: '輔導事業部',
            referral_department_id: '推薦事業部',
            rank_id: '聘階',
            referral_id: '推薦人',
            mentor_id: '輔導人',
            recruiter_id: '招募人'
        });
    } finally {
        createModalLoading.value = false;
    }
};

// 新增會員取消
const handleCreateCancel = () => {
    createFormRef.value.resetFields();
};

// 顯示更改密碼 Modal
const showChangePasswordModal = (record) => {
    if (!canShowPasswordAction(record)) {
        message.warning('此視圖無權限更改該會員密碼');
        return;
    }
    currentUser.value = record;
    changePasswordFormState.newPassword = '';
    changePasswordFormState.confirmPassword = '';
    changePasswordModalVisible.value = true;
};

// 更改密碼確認
const handleChangePasswordOk = async () => {
    try {
        // 驗證表單
        await changePasswordFormRef.value.validate();

        changePasswordModalLoading.value = true;

        const response = await resetUserPassword(currentUser.value.id, {
            password: changePasswordFormState.newPassword
        });

        if (response.result) {
            message.success('密碼更改成功');
            changePasswordModalVisible.value = false;
            changePasswordFormRef.value.resetFields();
            currentUser.value = null;
        } else {
            showErrorMessage(response.message || '密碼更改失敗');
        }
    } catch (error) {
        console.error('密碼更改失敗:', error);
        // 使用統一的錯誤處理函數
        handleApiError(error, '密碼更改失敗', {
            password: '密碼',
            newPassword: '新密碼',
            confirmPassword: '確認密碼'
        });
    } finally {
        changePasswordModalLoading.value = false;
    }
};

// 更改密碼取消
const handleChangePasswordCancel = () => {
    changePasswordFormRef.value.resetFields();
    currentUser.value = null;
};

// 顯示匯入 Modal
const showImportModal = () => {
    importFileList.value = [];
    importModalVisible.value = true;
};

// 匯入確認
const handleImportOk = async () => {
    if (importFileList.value.length === 0) {
        showErrorMessage('請選擇要匯入的檔案');
        return;
    }

    try {
        importModalLoading.value = true;
        const file = importFileList.value[0].originFileObj;
        const formData = new FormData();
        formData.append('file', file);
        // 檔案不大則用同步，大檔才走非同步 + 輪詢
        const useAsync =
            file &&
            typeof file.size === 'number' &&
            file.size >= IMPORT_ASYNC_THRESHOLD_BYTES;

        const response = await importUsers(formData, { useAsync });
        const data = response?.data || {};

        // 非同步模式：有任務 ID 即視為已建立匯入任務（支援 data.import_id / data.id 或 202 回傳體）
        const asyncTaskId =
            data.import_id != null
                ? data.import_id
                : data.id != null
                  ? data.id
                  : (response?.import_id ?? response?.id);
        if (useAsync && asyncTaskId != null) {
            addImportTask(asyncTaskId);
            const msg =
                response?.message && response.message !== 'success'
                    ? response.message
                    : '已建立匯入任務';
            message.success(msg);
            importModalVisible.value = false;
            importFileList.value = [];
            return;
        }

        if (response.result) {
            // 同步模式：處理 { success_count, created_count, updated_count, failure_count, failures, missing_data }
            const successCount = data.success_count || 0;
            const createdCount = data.created_count || 0;
            const updatedCount = data.updated_count || 0;
            const failureCount = data.failure_count || 0;
            const failures = data.failures || [];
            const missingData = data.missing_data || [];

            importResult.value = {
                status: data.status || 'completed',
                errorMessage: data.error_message || '',
                successCount,
                createdCount,
                updatedCount,
                failureCount,
                failures,
                missingData
            };

            // 完全成功：只顯示成功訊息，不顯示 Modal
            if (failureCount === 0 && missingData.length === 0) {
                let successMsg = `✅ 匯入成功！成功匯入 ${successCount} 筆資料`;
                if (createdCount > 0 || updatedCount > 0) {
                    const parts = [];
                    if (createdCount > 0) parts.push(`新增 ${createdCount} 筆`);
                    if (updatedCount > 0) parts.push(`更新 ${updatedCount} 筆`);
                    successMsg += `（${parts.join('、')}）`;
                }
                message.success(successMsg);
                importModalVisible.value = false;
                await loadUsers();
            } else {
                // 其他情況：顯示結果 Modal
                importModalVisible.value = false;
                importErrorsModalVisible.value = true;
            }
        } else {
            showErrorMessage(response.message || '匯入失敗');
        }
    } catch (error) {
        console.error('匯入失敗:', error);
        showErrorMessage('匯入失敗');
    } finally {
        importModalLoading.value = false;
    }
};

// 匯入取消
const handleImportCancel = () => {
    importFileList.value = [];
};

// 判斷匯入結果類型
const getImportResultType = () => {
    const { status, errorMessage, successCount, failureCount, missingData } =
        importResult.value;
    const hasFailures = failureCount > 0;
    const hasMissing = missingData && missingData.length > 0;
    const hasSuccess = successCount > 0;
    const taskFailed = status === 'failed' || Boolean(errorMessage);

    // 任務層級失敗（例如檔案遺失），沒有逐筆統計時仍應顯示失敗
    if (taskFailed && !hasSuccess && !hasFailures && !hasMissing) {
        return 'complete-failure';
    }
    if (!hasFailures && !hasMissing && !taskFailed) {
        return 'complete-success';
    }
    if (hasFailures && hasSuccess) {
        return 'partial-failure';
    }
    if (hasFailures && !hasSuccess) {
        return 'complete-failure';
    }
    if (hasMissing) {
        return 'missing-data';
    }
    if (taskFailed) {
        return 'complete-failure';
    }
    return 'unknown';
};

// 取得匯入結果標題
const getImportResultTitle = () => {
    const type = getImportResultType();
    switch (type) {
        case 'complete-success':
            return '✅ 匯入成功';
        case 'partial-failure':
            return '⚠️ 匯入完成，但有部分資料失敗';
        case 'complete-failure':
            return '❌ 匯入失敗，所有資料都無法匯入';
        case 'missing-data':
            return '⚠️ 缺少資料的身分證號碼清單';
        default:
            return '匯入結果';
    }
};

// 取得匯入結果圖標
const getImportResultIcon = () => {
    const type = getImportResultType();
    switch (type) {
        case 'complete-success':
            return CheckCircleOutlined;
        case 'partial-failure':
        case 'missing-data':
            return ExclamationCircleOutlined;
        case 'complete-failure':
            return CloseCircleOutlined;
        default:
            return CheckCircleOutlined;
    }
};

// 取得匯入結果 Modal 的 class
const getImportResultModalClass = () => {
    const type = getImportResultType();
    switch (type) {
        case 'complete-success':
            return 'import-success-modal';
        case 'partial-failure':
        case 'missing-data':
            return 'import-warning-modal';
        case 'complete-failure':
            return 'import-error-modal';
        default:
            return '';
    }
};

// 取得匯入結果狀態的 class
const getImportResultStatusClass = () => {
    const type = getImportResultType();
    switch (type) {
        case 'complete-success':
            return 'status-success';
        case 'partial-failure':
        case 'missing-data':
            return 'status-warning';
        case 'complete-failure':
            return 'status-error';
        default:
            return '';
    }
};

// 關閉匯入結果 Modal
const handleImportResultClose = () => {
    importErrorsModalVisible.value = false;
    // 如果有成功匯入，重新載入列表
    if (importResult.value.successCount > 0) {
        loadUsers();
    }
};

// 下載匯入範例
const handleDownloadTemplate = async () => {
    try {
        templateDownloadLoading.value = true;
        message.loading('正在下載範例檔案...', 0);

        const blob = await downloadImportTemplate();

        // 生成檔案名稱
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const fileName = `會員匯入範例_${year}-${month}-${day}_${hours}${minutes}${seconds}.csv`;

        // 創建下載連結
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);

        // 觸發下載
        document.body.appendChild(link);
        link.click();

        // 清理資源
        setTimeout(() => {
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        }, 100);

        message.destroy();
        message.success('範例檔案下載成功');
    } catch (error) {
        console.error('下載範例失敗:', error);
        message.destroy();
        const errorMessage = error.message || '下載範例失敗，請稍後再試';
        showErrorMessage(errorMessage);
    } finally {
        templateDownloadLoading.value = false;
    }
};

// 上傳前檢查
const beforeUpload = (file) => {
    const isValid =
        file.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv';
    if (!isValid) {
        showErrorMessage('只能上傳 Excel 或 CSV 檔案!');
        return false;
    }
    // 返回 false 阻止自動上傳，我們會在點擊確定按鈕時手動上傳
    return false;
};

// 查看聘階歷史
const viewRankHistory = async (record) => {
    try {
        currentUser.value = record;
        rankHistoryModalVisible.value = true;
        rankHistoryLoading.value = true;
        userRankHistory.value = [];

        const response = await getUserRankHistory(record.id);

        if (response && response.result) {
            // 檢查回應資料格式
            if (Array.isArray(response.data)) {
                userRankHistory.value = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
                userRankHistory.value = response.data.data;
            } else {
                console.warn('聘階歷史資料格式異常:', response);
                userRankHistory.value = [];
            }
        } else {
            console.warn('獲取聘階歷史失敗:', response);
            userRankHistory.value = [];
        }
    } catch (error) {
        console.error('載入聘階歷史失敗:', error);
        showErrorMessage('載入聘階歷史失敗');
        userRankHistory.value = [];
    } finally {
        rankHistoryLoading.value = false;
    }
};

// 格式化日期
const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        return date.toLocaleString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        return dateString;
    }
};

/** 起聘日期：列表只顯示年月日 */
const formatAssignedAtDisplay = (value) => {
    if (!value) return '-';
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        return s.slice(0, 10);
    }
    try {
        const date = new Date(s);
        if (Number.isNaN(date.getTime())) return s;
        return date.toLocaleDateString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    } catch (e) {
        return s;
    }
};

// 遮罩身分證
const maskIdNumber = (idNumber) => {
    if (!idNumber) return '-';
    if (idNumber.length <= 6) return idNumber;
    const start = idNumber.substring(0, 3);
    const end = idNumber.substring(idNumber.length - 3);
    return `${start}${'*'.repeat(idNumber.length - 6)}${end}`;
};

// 顯示身分證
const toggleIdNumber = (record) => {
    const userId = record.id;
    if (!idNumberVisible.value[userId]) {
        // 顯示身分證
        idNumberVisible.value[userId] = true;

        // 3 秒後自動隱藏
        setTimeout(() => {
            idNumberVisible.value[userId] = false;
        }, 3000);
    }
};

// 獲取性別標籤和圖標
const getGenderInfo = (gender) => {
    if (gender === 'male') {
        return { label: '男', icon: 'man', color: '#1890ff' };
    } else if (gender === 'female') {
        return { label: '女', icon: 'woman', color: '#eb2f96' };
    }
    return { label: '-', icon: null, color: '#8c8c8c' };
};

// 組織圖（列表操作欄）
const orgModalVisible = ref(false);
const orgChartLoading = ref(false);
const orgChartData = ref([]);
const orgLineType = ref('referral');
const orgChartKey = ref(0);
const orgChartRef = ref(null);
const orgChartUserId = ref(null);

const scheduleOrgChartFit = () => {
    nextTick(() => {
        setTimeout(() => {
            orgChartRef.value?.fitToView?.();
        }, 120);
    });
};

const openOrgChart = async (record) => {
    orgChartUserId.value = record?.id;
    orgModalVisible.value = true;
    await fetchOrgChart();
    scheduleOrgChartFit();
};

const onOrgLineTypeChange = async (val) => {
    orgLineType.value = val;
    await fetchOrgChart();
    scheduleOrgChartFit();
};

watch(
    () => orgModalVisible.value,
    (visible) => {
        if (visible) {
            scheduleOrgChartFit();
        }
    }
);

const fetchOrgChart = async () => {
    const userId = orgChartUserId.value;
    if (!userId) return;
    try {
        orgChartLoading.value = true;
        const res = await getUserOrganizationChart(userId, {
            type: orgLineType.value
        });
        let tree =
            res?.data?.data?.tree ||
            res?.data?.tree ||
            res?.tree ||
            res?.data?.data ||
            res?.data ||
            [];
        if (!Array.isArray(tree)) tree = [tree];

        const transformNode = (n) => ({
            name: n?.name || '-',
            rank_name: n?.rank_name || n?.rank?.name || '',
            rank_color:
                n?.rank_color || n?.rank?.color || n?.rank?.rank_color || '',
            referral_name: n?.referral_name || '',
            mentor_name: n?.mentor_name || '',
            assigned_at: n?.assigned_at || '',
            students_count: n?.students_count,
            raw: n,
            children: Array.isArray(n?.children)
                ? n.children.map((c) => transformNode(c))
                : []
        });
        orgChartData.value = tree.map((root) => transformNode(root));
        scheduleOrgChartFit();
    } catch (e) {
        orgChartData.value = [];
        showErrorMessage('載入組織圖失敗');
    } finally {
        orgChartLoading.value = false;
    }
};

// 匯出會員
const handleExport = async () => {
    try {
        message.loading('正在匯出會員資料...', 0);

        // 構建匯出參數（使用當前的篩選條件）
        const exportParams = {};

        // 添加篩選條件
        if (filters.department_id)
            exportParams.department_id = filters.department_id;
        if (filters.referral_department_id) {
            exportParams.referral_department_id = filters.referral_department_id;
        }
        if (filters.rank_id) exportParams.rank_id = filters.rank_id;
        if (filters.status !== undefined) exportParams.status = filters.status;
        if (filters.identity) exportParams.identity = filters.identity;
        if (filters.name) {
            if (useFuzzyNameFilter.value) {
                exportParams.name = filters.name;
            } else {
                exportParams.id = filters.name;
            }
        }
        if (filters.email) exportParams.email = filters.email;
        if (filters.phone) exportParams.phone = filters.phone;
        if (filters.id_number) exportParams.id_number = filters.id_number;
        if (filters.has_line !== undefined)
            exportParams.has_line = filters.has_line;
        if (filters.referral_id) exportParams.referral_id = filters.referral_id;
        if (filters.mentor_id) exportParams.mentor_id = filters.mentor_id;
        if (filters.assigned_at_from) {
            exportParams.assigned_at_from = filters.assigned_at_from;
        }
        if (filters.assigned_at_to) {
            exportParams.assigned_at_to = filters.assigned_at_to;
        }
        if (filters.referral_code) {
            exportParams.referral_code = filters.referral_code;
        }
        if (filters.deregistered !== undefined) {
            exportParams.deregistered = filters.deregistered;
        }

        const exportProfile = profileConfig.value.exportProfile;
        if (exportProfile && exportProfile !== 'full') {
            exportParams.export_profile = exportProfile;
        }

        // 調用 API 獲取 blob 資料
        const blob = await exportUsers(exportParams);

        // 生成檔案名稱：會員資料_YYYY-MM-DD_HHMMSS.csv
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const fileName = `會員資料_${year}-${month}-${day}_${hours}${minutes}${seconds}.csv`;

        // 創建下載連結
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);

        // 觸發下載
        document.body.appendChild(link);
        link.click();

        // 清理資源
        setTimeout(() => {
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        }, 100);

        message.destroy();
        message.success('匯出成功');
    } catch (error) {
        console.error('匯出失敗:', error);
        message.destroy();
        const errorMessage = error.message || '匯出失敗，請稍後再試';
        showErrorMessage(errorMessage);

        // 如果是格式錯誤，提示可能是後端問題
        if (
            errorMessage.includes('格式') ||
            errorMessage.includes('Content-Type') ||
            errorMessage.includes('非 Excel')
        ) {
            console.error(
                '匯出錯誤詳情：後端可能未正確返回 Excel 檔案格式，請檢查後端 API 實作'
            );
        }
    }
};

// 防止重複載入的標記
const isInitialized = ref(false);

// 初始化

onMounted(async () => {
    // 載入欄位顯示設置
    loadColumnSettings();
    // 還原本地匯入任務並對尚未結束的任務恢復輪詢
    const stored = loadImportTasksFromStorage();
    if (stored.length) {
        activeImportTasks.value = stored;
        stored
            .filter((t) => t.status === 'pending' || t.status === 'processing')
            .forEach((t) => pollImportStatus(t.import_id));
    }
    if (isInitialized.value) return;
    isInitialized.value = true;

    // 檢查 URL 查詢參數
    const nameParam = route.query.name;
    if (nameParam) {
        // 如果有 name 參數，設置搜尋條件
        filters.name = nameParam;
        // 開啟篩選面板
        filterActiveKeys.value = ['1'];
        message.info(`搜尋成員：${nameParam}`);
    }

    // 若從事業部頁面帶有 department_id，預先套用到篩選條件並展開篩選
    const deptFromQuery = route.query && route.query.department_id;
    if (deptFromQuery !== undefined) {
        const parsed = Number(deptFromQuery);
        filters.department_id = Number.isNaN(parsed) ? deptFromQuery : parsed;
        filterActiveKeys.value = ['1'];
    }

    // 若從聘階管理帶有 rank_id，預先套用到篩選條件並展開篩選
    const rankFromQuery = route.query && route.query.rank_id;
    if (rankFromQuery !== undefined) {
        const parsed = Number(rankFromQuery);
        filters.rank_id = Number.isNaN(parsed) ? rankFromQuery : parsed;
        filterActiveKeys.value = ['1'];
    }

    // 從儀表板帶入 LINE 綁定狀態，0 表示未綁定、1 表示已綁定
    const hasLineFromQuery = route.query && route.query.has_line;
    if (hasLineFromQuery === '0' || hasLineFromQuery === '1') {
        filters.has_line = Number(hasLineFromQuery);
        filterActiveKeys.value = ['1'];
    }

    // 先載入基礎資料，避免同時發起太多請求
    try {
        await Promise.all([
            loadLevels(),
            loadDepartments(),
            loadRanks(),
            loadDepartmentOptions(),
            loadRankOptions()
        ]);
        // 基礎資料載入完成後再載入用戶列表
        await loadUsers();
    } catch (error) {
        console.error('初始化載入資料失敗:', error);
        showErrorMessage('載入資料失敗，請重新整理頁面');
    }
});

onUnmounted(() => {
    Object.values(importPollTimers.value).forEach((id) => clearInterval(id));
    importPollTimers.value = {};
});

watch(
    () => route.name,
    (name, prevName) => {
        const listNames = [
            'MembersList',
            'MembersBusiness',
            'MembersGeneralAdmin'
        ];
        if (!listNames.includes(String(name))) {
            return;
        }
        // 僅在「會員列表各視角」之間切換時重載；從會員詳情等頁籤返回時保留篩選與分頁
        if (!listNames.includes(String(prevName))) {
            return;
        }
        pagination.current = 1;
        loadUsers();
    }
);
</script>

<style scoped>
.members-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

.toolbar {
    margin-bottom: 16px;
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.column-settings-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 8px;
    border: 1px solid rgba(200, 200, 210, 0.4);
    background: rgba(255, 255, 255, 0.9);
    color: #2a2a3a;
    transition: all 0.3s ease;
}

.column-settings-icon-btn:hover {
    border-color: rgba(42, 74, 122, 0.6);
    color: #2a4a7a;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2);
}

.column-settings-icon-btn :deep(.anticon) {
    font-size: 16px;
    margin: 0 !important;
    line-height: 1 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.filter-toggle-container {
    display: inline-block;
}

.filter-toggle-btn {
    border: 2px solid transparent;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9),
        rgba(248, 249, 250, 0.8)
    );
    color: #1a1a2a;
    font-family: 'Courier New', monospace;
    font-weight: 500;
    border-radius: 12px;
    padding: 8px 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.2);
    font-size: 14px;
}

.filter-toggle-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    opacity: 0.6;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.filter-toggle-btn:hover::before {
    opacity: 1;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.4);
}

.filter-toggle-btn.active {
    color: #2a4a7a;
}

.filter-toggle-btn.active::before {
    opacity: 1;
}

.filter-panel {
    margin-bottom: 24px;
}

.filter-panel :deep(.ant-collapse-item) {
    background: transparent !important;
    border: none !important;
}

.filter-panel :deep(.ant-collapse-content) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(42, 74, 122, 0.2) !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.1) !important;
    overflow: hidden !important;
}

.filter-panel :deep(.ant-collapse-header) {
    display: none !important;
}

.filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
}

.clickable-link {
    cursor: pointer;
    color: #2a2a3a;
}

.clickable-link:hover {
    color: #2a4a7a;
    text-decoration: underline;
}

.filter-header {
    font-size: 16px;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    color: #1a1a2a;
}

.filter-content {
    padding: 20px 0;
}

.filter-content .filter-items-full {
    margin-bottom: 0;
}

.filter-items-full {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    width: 100%;
}

.filter-actions-bottom {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 24px;
}

.filters :deep(.vaporwave-select .ant-select-selector) {
    /* 選擇器容器樣式 */
    position: relative;
    height: 44px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9),
        rgba(248, 249, 250, 0.8)
    ) !important;
    border: 2px solid transparent !important;
    border-radius: 12px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.2) !important;
    padding: 0 12px !important;
    display: flex;
    align-items: center;
}

.filters :deep(.vaporwave-select .ant-select-selector::before) {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    pointer-events: none;
}

.filters :deep(.vaporwave-select .ant-select-selection-search-input) {
    line-height: 40px !important;
    height: 40px !important;
    font-size: 14px !important;
    font-family: 'Courier New', monospace !important;
    color: #1a1a2a !important;
}

.filters :deep(.vaporwave-select .ant-select-selection-item) {
    line-height: 40px !important;
    height: 40px !important;
    color: #1a1a2a !important;
    font-size: 14px !important;
    font-family: 'Courier New', monospace !important;
}

.filters :deep(.vaporwave-select .ant-select-selection-placeholder) {
    line-height: 40px !important;
    height: 40px !important;
    color: rgba(0, 0, 0, 0.6) !important;
    opacity: 1 !important;
    font-size: 14px !important;
    font-family: 'Courier New', monospace !important;
}

.filters :deep(.vaporwave-select .ant-select-arrow) {
    color: #2a4a7a !important;
}

.filters :deep(.h-\[40px\]) {
    height: 40px !important;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9),
        rgba(248, 249, 250, 0.8)
    ) !important;
    border: 2px solid transparent !important;
    border-radius: 12px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.2) !important;
    padding: 0 12px !important;
    position: relative;
}

.filters :deep(.h-\[40px\]::before) {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    pointer-events: none;
    z-index: -1;
}

.filters :deep(.h-\[40px\] input) {
    font-size: 14px !important;
    font-family: 'Courier New', monospace !important;
    color: #1a1a2a !important;
}

.filters :deep(.ant-input-wrapper) {
    position: relative;
}

.filters :deep(.ant-input:not(.filter-input)) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 2px solid transparent !important;
    border-radius: 12px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.2) !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 16px !important;
    height: 44px !important;
    padding: 0 16px !important;
    position: relative;
    z-index: 1;
}

/* allow-clear 會包成 affix-wrapper：外框只留一層，避免內外雙框 */
.filters :deep(.ant-input-affix-wrapper) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
    padding-inline: 11px !important;
}

.filters :deep(.ant-input-affix-wrapper:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.filters :deep(.ant-input-affix-wrapper-focused),
.filters :deep(.ant-input-affix-wrapper:focus-within) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

.filters :deep(.ant-input-affix-wrapper .ant-input),
.filters :deep(.ant-input-affix-wrapper .ant-input.filter-input) {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    height: auto !important;
    padding: 0 !important;
    font-size: 13px !important;
}

.filters :deep(.ant-input-wrapper::before) {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    pointer-events: none;
    z-index: 0;
}

.filters :deep(.ant-input:hover),
.filters :deep(.ant-input:focus) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 2px solid transparent !important;
    box-shadow: 0 0 25px rgba(42, 74, 122, 0.3) !important;
}

.filters :deep(.h-\[40px\] input),
.filters :deep(.h-\[40px\] .ant-input),
.filters :deep(.ant-input) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
}

.search-box {
    flex: 1;
    max-width: 400px;
}

.members-table {
    position: relative;
    z-index: 1;
}

/* 表格 hover 效果禁用樣式已移動到 DataTable 組件中 */

.user-info {
    display: flex;
    flex-direction: column;
}

.user-name-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.user-name {
    font-weight: 500;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.gender-icon {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
}

.gender-icon-content {
    font-size: 14px;
}

.user-email {
    font-size: 12px;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
}

.verified-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.verified-icon {
    display: inline-flex;
    align-items: center;
    font-size: 16px;
}

.id-number-cell {
    display: flex;
    align-items: center;
    gap: 4px;
}

.eye-icon {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    color: #8c8c8c;
    cursor: pointer;
    transition: color 0.3s;
    padding: 0 2px;
}

.eye-icon:hover {
    color: #595959;
}

.email-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.phone-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.birthday-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.id-number-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.rank-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.department-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.mentor-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.referral-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.exp-value {
    font-weight: 600;
    color: #0e7490;
    font-family: 'Courier New', monospace;
}

.level-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 500;
}

/* 聘階標籤為淡色/白色時：黑框黑字避免看不到 */
/* 淡色聘階：強制深色字（需 :deep，否則 scoped 吃不到 a-tag 內部） */
:deep(.rank-tag-light-bg.ant-tag) {
    color: #1f1f1f !important;
    border: 1px solid rgba(31, 31, 31, 0.35) !important;
}
:deep(.rank-tag-light-bg.ant-tag *) {
    color: #1f1f1f !important;
}

/* 近白聘階（白星）：白底表格上仍看得出膠囊外框 */
:deep(.rank-tag-near-white.ant-tag) {
    background: #f3f4f6 !important;
    border: 1px solid #9ca3af !important;
    color: #1f2937 !important;
}
:deep(.rank-tag-near-white.ant-tag *) {
    color: #1f2937 !important;
}

/* DataTable component integration */
:deep(.ant-card) {
    background: transparent;
    border: none;
}

:deep(.ant-card-body) {
    padding: 0;
}

.detail-content {
    padding: 16px 0;
}

/* 工具欄按鈕樣式 */
:deep(.ant-btn) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
}

:deep(.ant-btn:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

:deep(.ant-btn-primary) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
}

:deep(.ant-btn-primary:hover) {
    background: linear-gradient(45deg, #3b6ea5, #2a4a7a) !important;
}

:deep(.ant-btn .anticon) {
    color: #2a2a3a !important;
}

:deep(.ant-btn:hover .anticon) {
    color: #0e7490 !important;
}

/* 標籤樣式已移動到 DataTable 組件中 */

/* Modal 樣式 */
:deep(.ant-modal) {
    background: linear-gradient(135deg, #0d0d0d, #1a1a2e) !important;
    border: 2px solid transparent !important;
    border-radius: 16px !important;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3) !important;
}

:deep(.ant-modal-content) {
    background: linear-gradient(135deg, #0d0d0d, #1a1a2e) !important;
    border-radius: 16px !important;
}

:deep(.ant-modal-header) {
    background: transparent !important;
    border-bottom: 1px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 16px 16px 0 0 !important;
}

:deep(.ant-modal-title) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
}

:deep(.ant-modal-body) {
    background: transparent !important;
    color: #2a2a3a !important;
}

:deep(.ant-modal-footer) {
    background: transparent !important;
    border-top: 1px solid rgba(168, 255, 246, 0.3) !important;
    border-radius: 0 0 16px 16px !important;
}

/* 表單樣式 */
:deep(.ant-form-item-label > label) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-input) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
}

:deep(.ant-input:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

:deep(.ant-input:focus) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    box-shadow: 0 0 0 2px rgba(168, 255, 246, 0.2) !important;
}

:deep(.ant-input-number) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
}

:deep(.ant-input-number:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

:deep(.ant-input-number:focus) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    box-shadow: 0 0 0 2px rgba(168, 255, 246, 0.2) !important;
}

:deep(.ant-textarea) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
}

:deep(.ant-textarea:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

:deep(.ant-textarea:focus) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    box-shadow: 0 0 0 2px rgba(168, 255, 246, 0.2) !important;
}

/* 單選按鈕樣式 */
:deep(.ant-radio-group) {
    color: #2a2a3a !important;
}

:deep(.ant-radio-wrapper) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-radio-wrapper:hover) {
    color: #0e7490 !important;
}

:deep(.ant-radio-inner) {
    border-color: rgba(200, 200, 210, 0.4) !important;
}

:deep(.ant-radio-checked .ant-radio-inner) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    background-color: #0e7490 !important;
}

:deep(.ant-radio-checked .ant-radio-inner::after) {
    background-color: rgba(255, 255, 255, 0.9) !important;
}

/* 分隔線樣式 */
:deep(.ant-divider) {
    border-color: rgba(200, 200, 210, 0.4) !important;
}

/* 描述列表樣式 */
:deep(.ant-descriptions) {
    background: transparent !important;
}

:deep(.ant-descriptions-item-label) {
    background: rgba(255, 255, 255, 0.8) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
}

:deep(.ant-descriptions-item-content) {
    background: rgba(255, 255, 255, 0.8) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
}

/* Input 樣式 - 確保文字可見 */
:deep(.ant-input-number) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-input-number-input) {
    background: transparent !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-input-number-input::placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    opacity: 1 !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-input) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-textarea) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-textarea::placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    opacity: 1 !important;
    font-family: 'Courier New', monospace !important;
}

/* 上傳組件樣式 */
:deep(.ant-upload-dragger) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 2px dashed rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
}

:deep(.ant-upload-dragger:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
}

:deep(.ant-upload-drag-icon) {
    color: #0e7490 !important;
}

:deep(.ant-upload-text) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-upload-hint) {
    color: rgba(42, 42, 58, 0.7) !important;
    font-family: 'Courier New', monospace !important;
}

/* 匯入 Modal 樣式 */
.import-modal-content {
    padding: 8px 0;
}

.template-download-section {
    text-align: center;
    margin-bottom: 16px;
}

.template-download-btn {
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    margin-bottom: 8px;
}

.template-hint {
    margin: 0;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

.import-modal-content :deep(.ant-divider) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    margin: 16px 0 !important;
}

/* 匯入任務常駐通知（右側）：可展開/收合 */
.import-tasks-panel {
    position: fixed;
    top: 80px;
    right: 24px;
    width: 180px;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.15);
    z-index: 1050;
    font-family: 'Courier New', monospace;
    overflow: hidden;
    transition:
        width 0.2s ease,
        box-shadow 0.2s ease;
}

.import-tasks-panel.is-expanded {
    width: 300px;
    max-height: calc(100vh - 120px);
    box-shadow: 0 4px 24px rgba(42, 74, 122, 0.2);
    display: flex;
    flex-direction: column;
}

.import-tasks-panel-header {
    padding: 8px 12px;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.12),
        rgba(138, 175, 255, 0.08)
    );
    border-bottom: 1px solid transparent;
    font-weight: 600;
    font-size: 13px;
    color: #2a2a3a;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    user-select: none;
}

.import-tasks-panel.is-expanded .import-tasks-panel-header {
    padding: 10px 14px;
    border-bottom-color: rgba(42, 74, 122, 0.2);
}

.import-tasks-panel-header:hover {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.18),
        rgba(138, 175, 255, 0.12)
    );
}

.import-tasks-panel-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.import-tasks-chevron {
    flex-shrink: 0;
    font-size: 12px;
    color: rgba(42, 42, 58, 0.6);
}

.import-tasks-list {
    padding: 10px 12px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 280px;
}

.import-task-card {
    padding: 12px;
    background: rgba(248, 249, 250, 0.9);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
}

.import-task-card.is-done {
    border-color: rgba(200, 200, 210, 0.4);
}

.import-task-title {
    font-weight: 600;
    color: #2a2a3a;
    margin-bottom: 4px;
}

.import-task-status {
    font-size: 13px;
    color: rgba(42, 42, 58, 0.8);
    margin-bottom: 8px;
}

.import-task-counts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 12px;
}

.import-task-counts .count.success {
    color: #52c41a;
}

.import-task-counts .count.fail {
    color: #ff4d4f;
}

.import-task-error {
    font-size: 12px;
    color: #ff4d4f;
    margin-bottom: 8px;
    word-break: break-word;
}

.import-task-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.import-task-btn {
    font-family: 'Courier New', monospace !important;
    font-size: 12px !important;
    height: 26px !important;
    padding: 0 12px !important;
    border-radius: 999px !important;
}

.import-task-dismiss {
    margin-top: 4px;
}

/* 匯入結果 Modal 樣式 */
:deep(.import-errors-modal .ant-modal-content) {
    background: #ffffff;
    border: 2px solid #ffa87a;
    box-shadow: 0 0 40px rgba(255, 168, 122, 0.4);
}

:deep(.import-success-modal .ant-modal-content) {
    border: 2px solid #52c41a;
    box-shadow: 0 0 40px rgba(82, 196, 26, 0.4);
}

:deep(.import-warning-modal .ant-modal-content) {
    border: 2px solid #faad14;
    box-shadow: 0 0 40px rgba(250, 173, 20, 0.4);
}

:deep(.import-error-modal .ant-modal-content) {
    border: 2px solid #ff4d4f;
    box-shadow: 0 0 40px rgba(255, 77, 79, 0.4);
}

:deep(.import-errors-modal .ant-modal-header) {
    background: linear-gradient(
        135deg,
        rgba(255, 168, 122, 0.1),
        rgba(255, 158, 207, 0.1)
    );
    border-bottom: 2px solid #ffa87a;
}

:deep(.import-success-modal .ant-modal-header) {
    background: linear-gradient(
        135deg,
        rgba(82, 196, 26, 0.1),
        rgba(168, 255, 246, 0.1)
    );
    border-bottom: 2px solid #52c41a;
}

:deep(.import-warning-modal .ant-modal-header) {
    background: linear-gradient(
        135deg,
        rgba(250, 173, 20, 0.1),
        rgba(255, 168, 122, 0.1)
    );
    border-bottom: 2px solid #faad14;
}

:deep(.import-error-modal .ant-modal-header) {
    background: linear-gradient(
        135deg,
        rgba(255, 77, 79, 0.1),
        rgba(255, 168, 122, 0.1)
    );
    border-bottom: 2px solid #ff4d4f;
}

:deep(.import-errors-modal .ant-modal-title) {
    color: #ffa87a;
    font-family: 'Courier New', monospace;
    font-weight: bold;
    text-shadow: 0 0 10px rgba(255, 168, 122, 0.5);
}

:deep(.import-success-modal .ant-modal-title) {
    color: #52c41a;
    text-shadow: 0 0 10px rgba(82, 196, 26, 0.5);
}

:deep(.import-warning-modal .ant-modal-title) {
    color: #faad14;
    text-shadow: 0 0 10px rgba(250, 173, 20, 0.5);
}

:deep(.import-error-modal .ant-modal-title) {
    color: #ff4d4f;
    text-shadow: 0 0 10px rgba(255, 77, 79, 0.5);
}

.import-result-content {
    padding: 8px 0;
}

/* 狀態訊息 */
.result-status-message {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 24px;
    font-family: 'Courier New', monospace;
}

.result-status-message.status-success {
    background: rgba(82, 196, 26, 0.1);
    border: 2px solid rgba(82, 196, 26, 0.3);
}

.result-status-message.status-warning {
    background: rgba(250, 173, 20, 0.1);
    border: 2px solid rgba(250, 173, 20, 0.3);
}

.result-status-message.status-error {
    background: rgba(255, 77, 79, 0.1);
    border: 2px solid rgba(255, 77, 79, 0.3);
}

.status-icon {
    font-size: 32px;
    flex-shrink: 0;
}

.status-success .status-icon {
    color: #52c41a;
}

.status-warning .status-icon {
    color: #faad14;
}

.status-error .status-icon {
    color: #ff4d4f;
}

.status-text {
    flex: 1;
}

.status-title {
    font-size: 18px;
    font-weight: bold;
    color: #2a2a3a;
    margin-bottom: 8px;
}

.status-detail {
    font-size: 14px;
    color: #6a6a7a;
}

.result-summary {
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-bottom: 20px;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    border-radius: 12px;
    font-family: 'Courier New', monospace;
    flex: 1;
    transition: all 0.3s ease;
}

.summary-item.created-info {
    background: rgba(82, 196, 26, 0.1);
    border: 2px solid rgba(82, 196, 26, 0.3);
}

.summary-item.updated-info {
    background: rgba(138, 175, 255, 0.1);
    border: 2px solid rgba(138, 175, 255, 0.3);
}

.summary-item.error-info {
    background: rgba(255, 168, 122, 0.1);
    border: 2px solid rgba(255, 168, 122, 0.3);
}

.summary-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(42, 74, 122, 0.2);
}

.success-icon {
    font-size: 24px;
    color: #52c41a;
}

.created-icon {
    font-size: 24px;
    color: #52c41a;
}

.updated-icon {
    font-size: 24px;
    color: #3b6ea5;
}

.error-icon {
    font-size: 24px;
    color: #ffa87a;
}

.summary-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
}

.summary-label {
    font-size: 14px;
    color: #6a6a7a;
    font-weight: 500;
}

.summary-value {
    font-size: 28px;
    font-weight: bold;
    color: #2a2a3a;
    line-height: 1;
}

.summary-item.created-info .summary-value {
    color: #52c41a;
}

.summary-item.updated-info .summary-value {
    color: #3b6ea5;
}

.summary-item.error-info .summary-value {
    color: #ffa87a;
}

.summary-unit {
    font-size: 14px;
    color: #6a6a7a;
    margin-left: 4px;
}

.errors-section,
.missing-data-section {
    margin-bottom: 24px;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ffa87a;
    font-size: 18px;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    margin-bottom: 16px;
    text-shadow: 0 0 10px rgba(255, 168, 122, 0.4);
}

.title-icon {
    font-size: 20px;
}

.errors-container {
    max-height: 400px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.error-item {
    padding: 12px 16px;
    background: linear-gradient(
        135deg,
        rgba(255, 168, 122, 0.05),
        rgba(255, 158, 207, 0.05)
    );
    border: 1px solid rgba(255, 168, 122, 0.3);
    border-radius: 8px;
    transition: all 0.3s ease;
}

.error-item:hover {
    border-color: #ffa87a;
    box-shadow: 0 0 15px rgba(255, 168, 122, 0.2);
    transform: translateX(4px);
}

.error-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
    font-family: 'Courier New', monospace;
    flex-wrap: wrap;
}

.error-index {
    color: #ffa87a;
    font-weight: bold;
    font-size: 14px;
}

.error-row {
    color: #2a4a7a;
    font-size: 13px;
    font-weight: 500;
    padding: 2px 8px;
    background: rgba(42, 74, 122, 0.1);
    border-radius: 4px;
}

.error-name {
    color: #2a2a3a;
    font-size: 14px;
    font-weight: 600;
    padding: 2px 8px;
    background: rgba(42, 42, 58, 0.05);
    border-radius: 4px;
}

.error-message {
    color: #2a2a3a;
    font-size: 14px;
    line-height: 1.6;
    font-family: 'Courier New', monospace;
    padding-left: 24px;
}

.error-reason-label {
    color: #6a6a7a;
    font-weight: 500;
}

.error-reason-text {
    color: #ff4d4f;
    font-weight: 600;
}

.missing-data-info {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    background: rgba(250, 173, 20, 0.05);
    border: 1px solid rgba(250, 173, 20, 0.2);
    border-radius: 8px;
    margin-bottom: 16px;
}

.info-icon {
    font-size: 20px;
    color: #faad14;
    flex-shrink: 0;
    margin-top: 2px;
}

.info-text {
    color: #6a6a7a;
    font-size: 13px;
    line-height: 1.6;
    font-family: 'Courier New', monospace;
    margin: 0;
}

.missing-data-container {
    max-height: 300px;
    overflow-y: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.missing-data-item {
    padding: 8px 12px;
    background: rgba(250, 173, 20, 0.1);
    border: 1px solid rgba(250, 173, 20, 0.3);
    border-radius: 6px;
    font-family: 'Courier New', monospace;
    transition: all 0.3s ease;
}

.missing-data-item:hover {
    border-color: #faad14;
    box-shadow: 0 0 10px rgba(250, 173, 20, 0.2);
    transform: translateY(-2px);
}

.missing-data-text {
    color: #2a2a3a;
    font-size: 13px;
    font-weight: 500;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 168, 122, 0.2);
}

/* 聘階歷史 Modal 樣式 */
.rank-history-content {
    padding: 16px 0;
}

.user-info-header {
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(168, 255, 246, 0.3);
}

.user-info-header h3 {
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

.user-info-header p {
    margin: 0;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.rank-history-table :deep(.ant-table) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(168, 255, 246, 0.3) !important;
    border-radius: 8px !important;
}

.rank-history-table :deep(.ant-table-thead > tr > th) {
    background: rgba(255, 255, 255, 0.9) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    border-bottom: 1px solid rgba(200, 200, 210, 0.4) !important;
}

.rank-history-table :deep(.ant-table-tbody > tr > td) {
    background: rgba(255, 255, 255, 0.6) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.rank-history-table :deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(42, 74, 122, 0.1) !important;
}

.reason-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.date-text {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

.empty-state {
    text-align: center;
    padding: 40px 20px;
}

.empty-state :deep(.ant-empty-description) {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
}

:deep(.rank-history-modal .ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    border: 2px solid transparent !important;
    border-radius: 16px !important;
    position: relative;
}

:deep(.rank-history-modal .ant-modal-content::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    z-index: -1;
}

:deep(.rank-history-modal .ant-modal-header) {
    background: transparent !important;
    border-bottom: 1px solid rgba(200, 200, 210, 0.4) !important;
}

:deep(.rank-history-modal .ant-modal-title) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

/* 新增會員 Modal 樣式 */
:deep(.create-member-modal .ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    border: 2px solid transparent !important;
    border-radius: 16px !important;
    position: relative;
}

:deep(.create-member-modal .ant-modal-content::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    z-index: -1;
}

:deep(.create-member-modal .ant-modal-header) {
    background: transparent !important;
    border-bottom: 1px solid rgba(200, 200, 210, 0.4) !important;
}

:deep(.create-member-modal .ant-modal-title) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
}

:deep(.create-member-modal .ant-modal-body) {
    background: transparent !important;
    color: #2a2a3a !important;
}

:deep(.create-member-modal .ant-modal-footer) {
    background: transparent !important;
    border-top: 1px solid rgba(168, 255, 246, 0.3) !important;
}

.create-member-form {
    padding: 8px 0;
}

.form-divider {
    margin: 24px 0 16px 0;
    border-color: rgba(42, 74, 122, 0.3) !important;
}

.form-divider .divider-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 14px;
}

/* 表單輸入欄位樣式 */
.create-member-form :deep(.ant-input),
.create-member-form :deep(.ant-input-number),
.create-member-form :deep(.ant-select-selector),
.create-member-form :deep(.ant-picker) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 2px solid transparent !important;
    border-radius: 12px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.2) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    position: relative;
    transition: all 0.3s ease !important;
}

.create-member-form :deep(.ant-input:hover),
.create-member-form :deep(.ant-input:focus),
.create-member-form :deep(.ant-input-number:hover),
.create-member-form :deep(.ant-input-number:focus),
.create-member-form :deep(.ant-select:hover .ant-select-selector),
.create-member-form :deep(.ant-select-focused .ant-select-selector),
.create-member-form :deep(.ant-picker:hover),
.create-member-form :deep(.ant-picker-focused) {
    border-color: transparent !important;
    box-shadow: 0 0 25px rgba(42, 74, 122, 0.4) !important;
}

.create-member-form :deep(.ant-input::placeholder),
.create-member-form :deep(.ant-picker-input > input::placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    font-family: 'Courier New', monospace !important;
}

.create-member-form :deep(.ant-select-selection-placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    font-family: 'Courier New', monospace !important;
}

.create-member-form :deep(.ant-form-item-label > label) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
}

.create-member-form :deep(.ant-select-arrow) {
    color: #2a4a7a !important;
}

.create-member-form :deep(.ant-picker-suffix) {
    color: #2a4a7a !important;
}

/* Switch 樣式 */
.create-member-form :deep(.ant-switch) {
    background-color: rgba(42, 74, 122, 0.3) !important;
}

.create-member-form :deep(.ant-switch-checked) {
    background-color: #2a4a7a !important;
}

.create-member-form :deep(.ant-switch-checked:hover) {
    background-color: #3b6ea5 !important;
}

/* 按鈕樣式 */
.create-member-modal :deep(.ant-btn-primary) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
}

.create-member-modal :deep(.ant-btn-primary:hover) {
    background: linear-gradient(45deg, #3b6ea5, #2a4a7a) !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.4) !important;
}

.create-member-modal :deep(.ant-btn-default) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.create-member-modal :deep(.ant-btn-default:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(200, 200, 210, 0.4) !important;
    color: #2a2a3a !important;
}

.org-field-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.org-copy-link {
    font-size: 12px;
    font-weight: 500;
    color: #9b6dff;
    text-decoration: none;
    line-height: 1;
    white-space: nowrap;
}

.org-copy-link:hover {
    color: #7a4dff;
    text-decoration: underline;
}

.org-copy-link.is-disabled {
    color: rgba(42, 42, 58, 0.28);
    pointer-events: none;
    text-decoration: none;
    cursor: not-allowed;
}

/* 帳號密碼顯示 Modal 樣式 */
:deep(.account-password-modal .ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    border: 2px solid transparent !important;
    border-radius: 16px !important;
    position: relative;
}

:deep(.account-password-modal .ant-modal-content::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    z-index: -1;
}

:deep(.account-password-modal .ant-modal-header) {
    background: transparent !important;
    border-bottom: 1px solid rgba(200, 200, 210, 0.4) !important;
}

:deep(.account-password-modal .ant-modal-title) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
}

:deep(.account-password-modal .ant-modal-body) {
    background: transparent !important;
    color: #2a2a3a !important;
}

.account-info-content {
    padding: 8px 0;
    text-align: center;
}

.success-icon-wrapper {
    margin-bottom: 16px;
}

.success-icon {
    font-size: 64px;
    color: #52c41a;
    animation: scaleIn 0.3s ease-out;
}

@keyframes scaleIn {
    0% {
        transform: scale(0);
        opacity: 0;
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.success-title {
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 20px;
}

.success-subtitle {
    margin: 0 0 24px 0;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.account-info-card {
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    text-align: left;
}

.info-item {
    margin-bottom: 16px;
}

.info-item:last-child {
    margin-bottom: 0;
}

.info-label {
    display: block;
    margin-bottom: 8px;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 500;
    font-size: 14px;
}

.copy-input-group {
    display: flex;
    width: 100%;
}

.copy-input-group :deep(.ant-input),
.copy-input-group :deep(.ant-input-password) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 2px solid transparent !important;
    border-radius: 8px 0 0 8px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.2) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
}

.copy-input-group :deep(.ant-input-password .ant-input) {
    border-radius: 8px 0 0 8px !important;
}

.copy-btn {
    border-radius: 0 8px 8px 0 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    min-width: 80px;
}

.warning-box {
    background: rgba(255, 167, 122, 0.1);
    border: 1px solid rgba(255, 167, 122, 0.3);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 24px;
}

.warning-text {
    margin: 0;
    color: #ffa87a;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    text-align: center;
}

.modal-actions {
    margin-top: 24px;
}

.modal-actions :deep(.ant-btn) {
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    height: 44px !important;
}

/* 更改密碼 Modal 樣式 */
:deep(.change-password-modal .ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    border: 2px solid transparent !important;
    border-radius: 16px !important;
    position: relative;
}

:deep(.change-password-modal .ant-modal-content::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    z-index: -1;
}

:deep(.change-password-modal .ant-modal-header) {
    background: transparent !important;
    border-bottom: 1px solid rgba(200, 200, 210, 0.4) !important;
}

:deep(.change-password-modal .ant-modal-title) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
}

:deep(.change-password-modal .ant-modal-body) {
    background: transparent !important;
    color: #2a2a3a !important;
}

.change-password-content {
    padding: 8px 0;
}

.user-info-text {
    margin: 0 0 16px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.user-info-text strong {
    font-weight: 600;
}

.change-password-content :deep(.ant-divider) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    margin: 16px 0 !important;
}

.change-password-content :deep(.ant-form-item-label > label) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
}

.change-password-content :deep(.ant-input-password) {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 2px solid transparent !important;
    border-radius: 12px !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.2) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    transition: all 0.3s ease !important;
}

.change-password-content :deep(.ant-input-password .ant-input) {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-family: 'Courier New', monospace !important;
    color: #2a2a3a !important;
}

.change-password-content :deep(.ant-input-password .ant-input::placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    font-family: 'Courier New', monospace !important;
    opacity: 1 !important;
}

.change-password-content
    :deep(.ant-input-password .ant-input::-webkit-input-placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    font-family: 'Courier New', monospace !important;
}

.change-password-content
    :deep(.ant-input-password .ant-input::-moz-placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    font-family: 'Courier New', monospace !important;
    opacity: 1 !important;
}

.change-password-content
    :deep(.ant-input-password .ant-input:-ms-input-placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    font-family: 'Courier New', monospace !important;
}

.change-password-content :deep(.ant-input-password:hover),
.change-password-content :deep(.ant-input-password:focus) {
    border-color: transparent !important;
    box-shadow: 0 0 25px rgba(42, 74, 122, 0.4) !important;
}

.change-password-content :deep(.ant-input-password::placeholder) {
    color: rgba(0, 0, 0, 0.6) !important;
    font-family: 'Courier New', monospace !important;
}

/* 確保查詢和重置按鈕樣式優先 */
.filter-actions .unified-search-btn,
.filter-actions .unified-search-btn.ant-btn,
.filter-actions .unified-search-btn.ant-btn-default,
.filter-actions :deep(.unified-search-btn),
.filter-actions :deep(.unified-search-btn.ant-btn),
.filter-actions :deep(.unified-search-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #7a9ba5 !important;
    color: #7a9ba5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
}

.filter-actions .unified-search-btn:hover,
.filter-actions .unified-search-btn.ant-btn:hover,
.filter-actions .unified-search-btn.ant-btn-default:hover,
.filter-actions :deep(.unified-search-btn:hover),
.filter-actions :deep(.unified-search-btn.ant-btn:hover),
.filter-actions :deep(.unified-search-btn.ant-btn-default:hover) {
    background: rgba(122, 155, 165, 0.1) !important;
    border-color: #7a9ba5 !important;
    color: #7a9ba5 !important;
    box-shadow: 0 2px 8px rgba(122, 155, 165, 0.3) !important;
}

.filter-actions .unified-reset-btn,
.filter-actions .unified-reset-btn.ant-btn,
.filter-actions .unified-reset-btn.ant-btn-default,
.filter-actions :deep(.unified-reset-btn),
.filter-actions :deep(.unified-reset-btn.ant-btn),
.filter-actions :deep(.unified-reset-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #c4a5a5 !important;
    color: #c4a5a5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
}

.filter-actions .unified-reset-btn:hover,
.filter-actions .unified-reset-btn.ant-btn:hover,
.filter-actions .unified-reset-btn.ant-btn-default:hover,
.filter-actions :deep(.unified-reset-btn:hover),
.filter-actions :deep(.unified-reset-btn.ant-btn:hover),
.filter-actions :deep(.unified-reset-btn.ant-btn-default:hover) {
    background: rgba(196, 165, 165, 0.1) !important;
    border-color: #c4a5a5 !important;
    color: #c4a5a5 !important;
    box-shadow: 0 2px 8px rgba(196, 165, 165, 0.3) !important;
}

.action-buttons :deep(.ant-space-item) {
    margin-right: 4px !important;
}

/* Filter 輸入框統一樣式 - 使用 Ant Design */
.filter-content .filters :deep(.filter-input.ant-input),
.filters :deep(.filter-input.ant-input) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
}

.filter-content .filters :deep(.filter-input:hover),
.filters :deep(.filter-input:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.filter-content .filters :deep(.filter-input:focus),
.filters :deep(.filter-input:focus) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

/* 選擇器樣式 - 與管理員管理一致 */
.filter-content .filters :deep(.status-select .ant-select),
.filters :deep(.status-select .ant-select) {
    border-radius: 8px !important;
}

.filter-content .filters :deep(.status-select .ant-select-selector),
.filters :deep(.status-select .ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
}

.filter-content .filters :deep(.status-select .ant-select-selector:hover),
.filters :deep(.status-select .ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.filter-content
    .filters
    :deep(.status-select .ant-select-focused .ant-select-selector),
.filters :deep(.status-select .ant-select-focused .ant-select-selector) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

.filter-content .filters :deep(.status-select .ant-select-selection-item),
.filters :deep(.status-select .ant-select-selection-item) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.filter-content
    .filters
    :deep(.status-select .ant-select-selection-placeholder),
.filters :deep(.status-select .ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: rgba(42, 42, 58, 0.5) !important;
    font-family: 'Courier New', monospace !important;
}

.filter-content .filters :deep(.status-select .ant-select-arrow),
.filters :deep(.status-select .ant-select-arrow) {
    color: rgba(42, 42, 58, 0.6) !important;
    transition: color 0.3s ease !important;
}

.filter-content
    .filters
    :deep(.status-select .ant-select:hover .ant-select-arrow),
.filters :deep(.status-select .ant-select:hover .ant-select-arrow) {
    color: #2a4a7a !important;
}

/* 下拉選單樣式 */
.filter-content .filters :deep(.status-select .ant-select-dropdown),
.filters :deep(.status-select .ant-select-dropdown) {
    background: rgba(255, 255, 255, 0.98) !important;
    backdrop-filter: blur(10px) !important;
    border: 1.5px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 12px !important;
    box-shadow: 0 0 25px rgba(42, 74, 122, 0.25) !important;
    padding: 8px !important;
    margin-top: 4px !important;
}

.filter-content .filters :deep(.status-select .ant-select-item),
.filters :deep(.status-select .ant-select-item) {
    background: transparent !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    border-radius: 8px !important;
    margin: 2px 0 !important;
    padding: 8px 12px !important;
    transition: all 0.2s ease !important;
}

.filter-content .filters :deep(.status-select .ant-select-item:hover),
.filters :deep(.status-select .ant-select-item:hover) {
    background: rgba(42, 74, 122, 0.15) !important;
    color: #2a4a7a !important;
}

.filter-content .filters :deep(.status-select .ant-select-item-option-selected),
.filters :deep(.status-select .ant-select-item-option-selected) {
    background: rgba(42, 74, 122, 0.25) !important;
    color: #2a4a7a !important;
    font-weight: 600 !important;
}

.filter-content .filters :deep(.status-select .ant-select-item-option-active),
.filters :deep(.status-select .ant-select-item-option-active) {
    background: rgba(42, 74, 122, 0.15) !important;
}

:deep(.ant-input::placeholder) {
    color: #000 !important;
    opacity: 1 !important;
}

:deep(input.ant-input::placeholder) {
    color: #000 !important;
    opacity: 1 !important;
}

/* 欄位設置 Modal 樣式 */
.column-settings-content {
    padding: 8px 0;
}

.column-settings-hint {
    margin: 0 0 20px 0;
    color: rgba(42, 42, 58, 0.7);
    font-family: 'Courier New', monospace;
    font-size: 13px;
}

.column-checkbox-group {
    width: 100%;
}

.column-checkbox-group :deep(.ant-checkbox-wrapper) {
    margin-bottom: 8px;
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
}

.column-checkbox-group :deep(.ant-checkbox-wrapper:hover) {
    color: #2a4a7a;
}

.column-checkbox-group :deep(.ant-checkbox-checked .ant-checkbox-inner) {
    background-color: #2a4a7a;
    border-color: #2a4a7a;
}

.column-checkbox-group :deep(.ant-checkbox:hover .ant-checkbox-inner) {
    border-color: #2a4a7a;
}

.column-checkbox-group :deep(.ant-checkbox-disabled) {
    cursor: not-allowed;
    opacity: 0.6;
}

.required-badge {
    margin-left: 4px;
    font-size: 11px;
    color: rgba(42, 42, 58, 0.6);
    font-style: italic;
}

.org-chart-modal-body {
    height: min(70vh, 640px);
    min-height: 480px;
}

.org-chart-modal-body :deep(.org-chart-wrapper) {
    height: 100%;
    min-height: 100%;
    border: none;
    border-radius: 0;
    background: transparent;
}
</style>
